import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import config from '../../config';
import s from './PaymentControlCenter.module.scss';
import { useCity } from '../../contexts/CityContext';
import { getDisplayBookingType } from '../../utils/bookingTypeDisplay';
import PaymentOverview from './PaymentOverview';

const PAYMENT_API = config.paymentApiBase;
const PRICING_API = `${config.dispatchApiBase}/payment`;
const POLLING_INTERVAL = 30000; // 30s auto-refresh

// ============================================================================
// HELPERS
// ============================================================================
const currency = (v) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(v) || 0);

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const instrumentLabel = (t) => {
  switch ((t || '').toUpperCase()) {
    case 'QR_CODE': return 'QR code';
    case 'MANUAL': return 'Manual entry';
    case 'PAYMENT_LINK': return 'Payment link';
    default: return '—';
  }
};

const statusColor = (st) => {
  switch ((st || '').toUpperCase()) {
    // Customer payment statuses
    case 'PAID': return 'paid';
    case 'ACTIVE': return 'active';
    case 'EXPIRED': return 'expired';
    // Driver payout statuses
    case 'SUCCESS': return 'success';
    case 'PENDING': return 'pending';
    case 'FAILED': return 'failed';
    case 'REJECTED': return 'rejected';
    case 'REVERSED': return 'reversed';
    default: return 'default';
  }
};

const displaySource = (src) => {
  const normalized = (src || '').toLowerCase();
  if (!normalized || normalized === 'ops' || normalized === 'whatsapp') return 'whatsapp';
  return normalized;
};

const installmentLabel = (type) => {
  switch ((type || 'FULL').toUpperCase()) {
    case 'FIRST': return '1st installment';
    case 'REMAINING': return 'Final installment';
    default: return null;
  }
};

const installmentBadgeStyle = (type) => {
  switch ((type || 'FULL').toUpperCase()) {
    case 'FIRST':
      return { background: '#fef3c7', color: '#92400e', borderColor: '#fcd34d' };
    case 'REMAINING':
      return { background: '#dbeafe', color: '#1e40af', borderColor: '#93c5fd' };
    default:
      return null;
  }
};

const renderInstallmentMeta = (txn) => {
  const label = installmentLabel(txn.installment_type);
  if (!label) return null;
  const bookingTotal = Number(txn.booking_total || 0);
  const style = installmentBadgeStyle(txn.installment_type);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
      <span className={s.attemptsBadge} style={{ ...style, alignSelf: 'flex-start', fontSize: '0.7rem' }}>
        {label}
      </span>
      {bookingTotal > 0 && (
        <span style={{ fontSize: '0.68rem', color: '#6b7280' }}>
          {currency(txn.amount)} of {currency(bookingTotal)} booking total
        </span>
      )}
    </div>
  );
};

// ============================================================================
// CHART HELPERS
// ============================================================================
const fmt_compact = (v) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR',
    notation: 'compact', maximumFractionDigits: 1,
  }).format(v || 0);

const percent = (v, digits = 1) => `${(Number(v) || 0).toFixed(digits)}%`;

const num = (v) => Number(v) || 0;

const toLocalDateTimeInput = (dt = new Date()) => {
  const d = new Date(dt);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

// ============================================================================
// INTERACTIVE TREND LINE CHART
// ============================================================================
const TrendLineChart = ({ data, granularity, chartMode = 'cumulative', onChartModeChange }) => {
  const [hoverIdx, setHoverIdx] = useState(null);
  const svgRef = useRef(null);

  const W = 1000, H = 300;
  const PAD = { l: 70, r: 20, t: 24, b: 48 };
  const cW = W - PAD.l - PAD.r;
  const cH = H - PAD.t - PAD.b;
  const n = data.length;
  if (!n) return null;

  const isPeriod = chartMode === 'period';
  const totalOf = (d) => isPeriod
    ? (d.period_paid_amount || 0) + (d.period_unpaid_amount || 0)
    : (d.paid_amount || 0) + (d.unpaid_amount || 0);
  const paidOf = (d) => isPeriod ? (d.period_paid_amount || 0) : (d.paid_amount || 0);

  const maxVal = Math.max(...data.map(totalOf), 1);
  const xOf = (i) => PAD.l + (n <= 1 ? cW / 2 : (i / (n - 1)) * cW);
  const yOf = (v) => PAD.t + cH - ((v || 0) / maxVal) * cH;
  const pts = (fn) => data.map((d, i) => `${xOf(i)},${fn(d)}`).join(' ');
  const area = (fn) => `${xOf(0)},${PAD.t + cH} ${pts(fn)} ${xOf(n - 1)},${PAD.t + cH}`;

  const yTicks = [0, 0.25, 0.5, 0.75, 1.0];
  const labelOf = (d) => {
    if (granularity === 'hourly') return d.label || d.date;
    return new Date(d.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };
  const step = Math.max(1, Math.ceil(n / 10));

  const handleMouseMove = (e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const scaleX = W / rect.width;
    const mx = (e.clientX - rect.left) * scaleX;
    let closest = 0, minDist = Infinity;
    data.forEach((_, i) => {
      const dist = Math.abs(xOf(i) - mx);
      if (dist < minDist) { minDist = dist; closest = i; }
    });
    setHoverIdx(closest);
  };

  const hd = hoverIdx !== null ? data[hoverIdx] : null;
  const hx = hoverIdx !== null ? xOf(hoverIdx) : null;
  const paidPct = hd ? (totalOf(hd) > 0 ? Math.round((paidOf(hd) / totalOf(hd)) * 100) : 0) : null;

  return (
    <div className={s.chartWrapper}>
      {/* Chart mode toggle */}
      <div className={s.chartModeRow}>
        <button
          className={`${s.chartModeBtn} ${!isPeriod ? s.chartModeBtnActive : ''}`}
          onClick={() => onChartModeChange('cumulative')}
        >
          Cumulative
        </button>
        <button
          className={`${s.chartModeBtn} ${isPeriod ? s.chartModeBtnActive : ''}`}
          onClick={() => onChartModeChange('period')}
        >
          {granularity === 'hourly' ? 'Per Hour' : 'Per Day'}
        </button>
      </div>

      {/* SVG Chart */}
      <div className={s.svgContainer}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          className={s.lineChart}
          preserveAspectRatio="xMidYMid meet"
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHoverIdx(null)}
        >
          <defs>
            <linearGradient id="gradPaid" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0.02" />
            </linearGradient>
            <linearGradient id="gradTotal" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#9ca3af" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#9ca3af" stopOpacity="0.02" />
            </linearGradient>
          </defs>

          {/* Grid lines + Y labels */}
          {yTicks.map((pct, i) => {
            const y = PAD.t + cH - pct * cH;
            return (
              <g key={i}>
                <line x1={PAD.l} y1={y} x2={W - PAD.r} y2={y}
                  stroke={pct === 0 ? '#d1d5db' : '#f0f0f0'} strokeWidth={pct === 0 ? 1.5 : 1} />
                <text x={PAD.l - 8} y={y + 4} textAnchor="end" fontSize="11" fill="#9ca3af">
                  {pct === 0 ? '₹0' : fmt_compact(maxVal * pct)}
                </text>
              </g>
            );
          })}

          {/* GREY SHADOW — total area */}
          {n > 1 && <polygon points={area((d) => yOf(totalOf(d)))} fill="url(#gradTotal)" />}
          {n > 1 && (
            <polyline points={pts((d) => yOf(totalOf(d)))}
              fill="none" stroke="#d1d5db" strokeWidth="1.5"
              strokeLinejoin="round" strokeLinecap="round"
              strokeDasharray="6 3" />
          )}

          {/* GREEN — paid area + line */}
          {n > 1 && <polygon points={area((d) => yOf(paidOf(d)))} fill="url(#gradPaid)" />}
          {n > 1 && (
            <polyline points={pts((d) => yOf(paidOf(d)))}
              fill="none" stroke="#10b981" strokeWidth="2.5"
              strokeLinejoin="round" strokeLinecap="round" />
          )}

          {/* Hover crosshair */}
          {hoverIdx !== null && (
            <>
              <line x1={hx} y1={PAD.t} x2={hx} y2={PAD.t + cH}
                stroke="#6b7280" strokeWidth="1" strokeDasharray="4 3" />
              {/* Total dot */}
              <circle cx={hx} cy={yOf(totalOf(hd))} r="5"
                fill="white" stroke="#9ca3af" strokeWidth="2" />
              {/* Paid dot */}
              <circle cx={hx} cy={yOf(paidOf(hd))} r="6"
                fill="#10b981" stroke="white" strokeWidth="2.5" />
            </>
          )}

          {/* Data dots (non-hover) */}
          {data.map((d, i) => hoverIdx === i ? null : (
            <circle key={i} cx={xOf(i)} cy={yOf(paidOf(d))} r="3.5"
              fill="#10b981" stroke="white" strokeWidth="1.5" opacity="0.7" />
          ))}

          {/* X-axis labels */}
          {data.map((d, i) =>
            i % step === 0 ? (
              <text key={i} x={xOf(i)} y={H - 8} textAnchor="middle" fontSize="11"
                fill={hoverIdx === i ? '#374151' : '#9ca3af'}
                fontWeight={hoverIdx === i ? '600' : '400'}>
                {labelOf(d)}
              </text>
            ) : null
          )}
        </svg>

        {/* Floating tooltip */}
        {hd && (
          <div className={s.chartTooltip}
            style={{
              left: `${Math.min(Math.max((hoverIdx / (n - 1)) * 100, 5), 80)}%`,
              transform: hoverIdx > n * 0.65 ? 'translateX(-105%)' : 'translateX(8px)',
            }}>
            <div className={s.ttLabel}>{labelOf(hd)}</div>
            <div className={s.ttRow}>
              <span className={s.ttDotGreen} />
              <span className={s.ttKey}>{isPeriod ? (granularity === 'hourly' ? 'This hour' : 'This day') : 'Cumulative collected'}</span>
              <span className={s.ttValGreen}>{currency(paidOf(hd))}</span>
            </div>
            <div className={s.ttRow}>
              <span className={s.ttDotGray} />
              <span className={s.ttKey}>{isPeriod ? 'Total potential' : 'Cumulative total'}</span>
              <span className={s.ttValGray}>{currency(totalOf(hd))}</span>
            </div>
            <div className={s.ttDivider} />
            <div className={s.ttRow}>
              <span className={s.ttKey} style={{ color: '#6b7280' }}>Collection rate</span>
              <span className={`${s.ttValGreen}`} style={{ fontWeight: 700 }}>{paidPct}%</span>
            </div>
            {isPeriod && (
              <div className={s.ttRow}>
                <span className={s.ttKey} style={{ color: '#ef4444' }}>Unpaid</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#ef4444' }}>
                  {currency((hd.period_unpaid_amount || 0))}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className={s.chartLegend}>
        <span className={s.legendItem}><span className={`${s.legendLine} ${s.legendLinePaid}`} /> Collected</span>
        <span className={s.legendItem}><span className={`${s.legendLine} ${s.legendLineTotal}`} /> Revenue potential</span>
        <span className={s.legendHint}>Hover over chart for details</span>
      </div>
    </div>
  );
};

// ============================================================================
// REVENUE RING (DONUT) CHART
// ============================================================================
const RevenueRing = ({ paid, unpaid, rate }) => {
  const total = paid + unpaid;
  const R = 54, r = 36, cx = 70, cy = 70;
  const circ = 2 * Math.PI * ((R + r) / 2);
  const paidArc = total > 0 ? (paid / total) * circ : 0;
  const mid = (R + r) / 2;

  return (
    <svg viewBox="0 0 140 140" className={s.ringChart}>
      <circle cx={cx} cy={cy} r={mid} fill="none" stroke="#f3f4f6" strokeWidth={R - r} />
      {paidArc > 0 && (
        <circle
          cx={cx} cy={cy} r={mid}
          fill="none"
          stroke="#10b981"
          strokeWidth={R - r}
          strokeDasharray={`${paidArc} ${circ - paidArc}`}
          strokeDashoffset={circ / 4}
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 0.6s ease' }}
        />
      )}
      <text x={cx} y={cy - 6} textAnchor="middle" fontSize="16" fontWeight="800" fill="#111827">
        {rate}%
      </text>
      <text x={cx} y={cy + 12} textAnchor="middle" fontSize="9" fill="#6b7280" fontWeight="600">
        COLLECTED
      </text>
    </svg>
  );
};

const LinkLifecycleDonut = ({ paid = 0, active = 0, expired = 0 }) => {
  const total = paid + active + expired;
  const cx = 76;
  const cy = 76;
  const r = 56;
  const c = 2 * Math.PI * r;

  const paidArc = total > 0 ? (paid / total) * c : 0;
  const activeArc = total > 0 ? (active / total) * c : 0;
  const expiredArc = total > 0 ? (expired / total) * c : 0;

  const paidOffset = 0;
  const activeOffset = paidArc;
  const expiredOffset = paidArc + activeArc;

  return (
    <div className={s.lifecycleWrap}>
      <svg viewBox="0 0 152 152" className={s.lifecycleDonut}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f3f4f6" strokeWidth="16" />
        {paidArc > 0 && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="#10b981"
            strokeWidth="16"
            strokeDasharray={`${paidArc} ${c - paidArc}`}
            strokeDashoffset={c / 4 - paidOffset}
            strokeLinecap="butt"
          />
        )}
        {activeArc > 0 && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="#3b82f6"
            strokeWidth="16"
            strokeDasharray={`${activeArc} ${c - activeArc}`}
            strokeDashoffset={c / 4 - activeOffset}
            strokeLinecap="butt"
          />
        )}
        {expiredArc > 0 && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="#9ca3af"
            strokeWidth="16"
            strokeDasharray={`${expiredArc} ${c - expiredArc}`}
            strokeDashoffset={c / 4 - expiredOffset}
            strokeLinecap="butt"
          />
        )}

        <text x={cx} y={cy - 2} textAnchor="middle" fontSize="20" fontWeight="800" fill="#111827">
          {total}
        </text>
        <text x={cx} y={cy + 16} textAnchor="middle" fontSize="10" fill="#6b7280" fontWeight="700">
          LINKS
        </text>
      </svg>

      <div className={s.lifecycleLegend}>
        <div className={s.lifecycleLegendItem}>
          <span className={`${s.lifecycleLegendDot} ${s.dotPaid}`} />
          <span className={s.lifecycleLegendKey}>Paid</span>
          <strong>{paid}</strong>
        </div>
        <div className={s.lifecycleLegendItem}>
          <span className={`${s.lifecycleLegendDot} ${s.dotActive}`} />
          <span className={s.lifecycleLegendKey}>Active</span>
          <strong>{active}</strong>
        </div>
        <div className={s.lifecycleLegendItem}>
          <span className={`${s.lifecycleLegendDot} ${s.dotExpired}`} />
          <span className={s.lifecycleLegendKey}>Expired</span>
          <strong>{expired}</strong>
        </div>
      </div>
    </div>
  );
};

const PeriodCollectionBars = ({ data = [], granularity = 'daily' }) => {
  const maxVal = Math.max(
    ...data.map((d) => num(d.paid) + num(d.unpaid)),
    1,
  );

  return (
    <div className={s.periodBarsWrap}>
      <div className={s.periodBarsChart}>
        {data.map((d, i) => {
          const paidH = (num(d.paid) / maxVal) * 100;
          const unpaidH = (num(d.unpaid) / maxVal) * 100;
          return (
            <div key={`${d.label}-${i}`} className={s.periodBarCol} title={`${d.label} · Paid ${currency(d.paid)} · Unpaid ${currency(d.unpaid)}`}>
              <div className={s.periodBarStack}>
                <div className={s.periodBarUnpaid} style={{ height: `${Math.max(0, unpaidH)}%` }} />
                <div className={s.periodBarPaid} style={{ height: `${Math.max(2, paidH)}%` }} />
              </div>
              <div className={s.periodBarLabel}>{d.label}</div>
            </div>
          );
        })}
      </div>
      <div className={s.periodBarsLegend}>
        <span className={s.legendItem}><span className={`${s.legendLine} ${s.legendLinePaid}`} /> Paid</span>
        <span className={s.legendItem}><span className={`${s.legendLine} ${s.legendLineTotal}`} /> Unpaid potential</span>
        <span className={s.legendHint}>{granularity === 'hourly' ? 'Hourly' : 'Daily'} period bars</span>
      </div>
    </div>
  );
};

// ============================================================================
// PERIOD SELECTOR (must be outside main component to avoid remount-on-render)
// ============================================================================
const PeriodSelector = ({ periodDays, customDays, setCustomDays, handlePresetPeriod, handleCustomDaysApply, handleCustomDaysKeyDown }) => (
  <div className={s.periodRow}>
    <span className={s.periodLabel}><i className="fa fa-calendar" /> Period:</span>
    {[1, 2, 3, 7, 14, 30, 60, 90].map((d) => (
      <button
        key={d}
        className={`${s.periodBtn} ${periodDays === d && !customDays ? s.periodActive : ''}`}
        onClick={() => handlePresetPeriod(d)}
      >
        {d === 1 ? 'Today' : `${d}d`}
      </button>
    ))}
    <div className={s.customDaysGroup}>
      <input
        className={s.customDaysInput}
        type="number"
        min="1"
        max="365"
        placeholder="Custom"
        value={customDays}
        onChange={(e) => setCustomDays(e.target.value)}
        onKeyDown={handleCustomDaysKeyDown}
      />
      <button className={s.customDaysBtn} onClick={handleCustomDaysApply} title="Apply custom days">
        <i className="fa fa-arrow-right" />
      </button>
    </div>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const PaymentControlCenter = ({ isOpen = false, onClose = () => { }, standalone = false }) => {
  const { selectedCity, selectedCities } = useCity();

  // Summary data
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [periodDays, setPeriodDays] = useState(90);
  const [customDays, setCustomDays] = useState('');
  const [periodStartDate, setPeriodStartDate] = useState('');
  const [periodEndDate, setPeriodEndDate] = useState('');

  // Transactions
  const [transactions, setTransactions] = useState([]);
  const [txnLoading, setTxnLoading] = useState(true);
  const [txnPage, setTxnPage] = useState(1);
  const [txnPageInput, setTxnPageInput] = useState(1);
  const [txnTotalPages, setTxnTotalPages] = useState(1);
  const [txnTotal, setTxnTotal] = useState(0);

  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCustomerId, setFilterCustomerId] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // Top-level view mode: 'all' shows everything, 'b2b' filters to B2B only
  const [viewMode, setViewMode] = useState('all'); // 'all' | 'whatsapp' | 'b2b'

  // Pending bookings
  const [pendingBookings, setPendingBookings] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [pendingPage, setPendingPage] = useState(1);
  const [pendingPageInput, setPendingPageInput] = useState(1);
  const [pendingTotalPages, setPendingTotalPages] = useState(1);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [selectedPendingBookings, setSelectedPendingBookings] = useState(new Set());
  const [generatingBulk, setGeneratingBulk] = useState(false);
  const [bulkLinkResult, setBulkLinkResult] = useState(null);

  // Detail view
  const [selectedTxn, setSelectedTxn] = useState(null);

  // Send payment link state
  const [sendingLinkFor, setSendingLinkFor] = useState(null);

  // Manual/offline payment state
  const [manualPaymentModal, setManualPaymentModal] = useState(null);
  const [manualPaySaving, setManualPaySaving] = useState(false);
  const [manualPayForm, setManualPayForm] = useState({
    mode: 'CASH',
    otherProvider: '',
    transactionTime: toLocalDateTimeInput(),
    gatewayTxnId: '',
    amount: '',
    notes: '',
  });

  // Pending tab customer filter
  const [pendingFilterCustomerId, setPendingFilterCustomerId] = useState(null);
  const [pendingFilterCustomerName, setPendingFilterCustomerName] = useState('');

  // Overview graph customer filter
  const [summaryCustomerInput, setSummaryCustomerInput] = useState('');
  const [summaryCustomerId, setSummaryCustomerId] = useState('');
  const [pendingRankMetric, setPendingRankMetric] = useState('amount'); // amount | count

  // Booking history for detail panel
  const [bookingHistory, setBookingHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Pricing tab
  const [pricingRows, setPricingRows] = useState([]);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingClassFilter, setPricingClassFilter] = useState('');
  const [pricingPayFilter, setPricingPayFilter] = useState(''); // '' | 'driver' | 'customer'
  const [pricingSavingId, setPricingSavingId] = useState(null);
  const [pricingEdits, setPricingEdits] = useState({});
  const [pricingPage, setPricingPage] = useState(1);
  const [pricingPageInput, setPricingPageInput] = useState(1);
  const [pricingTotalPages, setPricingTotalPages] = useState(1);
  const [pricingTotal, setPricingTotal] = useState(0);
  const [paymentBackendDown, setPaymentBackendDown] = useState(false);
  const [autoSendSettings, setAutoSendSettings] = useState({
    auto_send_consumer_at_pickup: false,
    auto_send_consumer_at_drop: false,
    auto_send_b2b_on_complete: false,
    auto_send_hourly_on_final_bill: false,
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSavingKey, setSettingsSavingKey] = useState('');

  // Driver Transactions Tab
  const getTodayStr = () => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().split('T')[0];
  };

  const getNinetyDaysAgoStr = () => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().split('T')[0];
  };

  const [driverTxns, setDriverTxns] = useState([]);
  const [driverTxnsLoading, setDriverTxnsLoading] = useState(false);
  const [driverTxnPage, setDriverTxnPage] = useState(1);
  const [driverTxnPageInput, setDriverTxnPageInput] = useState(1);
  const [driverTxnTotalPages, setDriverTxnTotalPages] = useState(1);
  const [driverTxnTotal, setDriverTxnTotal] = useState(0);
  const [driverTxnFilterStatus, setDriverTxnFilterStatus] = useState('');
  const [driverTxnFilterDateFrom, setDriverTxnFilterDateFrom] = useState(getNinetyDaysAgoStr());
  const [driverTxnFilterDateTo, setDriverTxnFilterDateTo] = useState(getTodayStr());
  const [driverTxnSearch, setDriverTxnSearch] = useState('');
  const [driverTxnView, setDriverTxnView] = useState('payouts'); // payouts | cash_paid
  const [cashPaidDrivers, setCashPaidDrivers] = useState([]);
  const [cashPaidLoading, setCashPaidLoading] = useState(false);
  const [cashPaidPage, setCashPaidPage] = useState(1);
  const [cashPaidPageInput, setCashPaidPageInput] = useState(1);
  const [cashPaidTotalPages, setCashPaidTotalPages] = useState(1);
  const [cashPaidTotal, setCashPaidTotal] = useState(0);

  // Chart mode
  const [chartMode, setChartMode] = useState('cumulative'); // cumulative | period

  // Active tab
  const [activeTab, setActiveTab] = useState('overview'); // overview | transactions | pending | pricing | driver_txns | financial_report

  // Financial report tab (Platform Fee / Marketing Discount, internal-only, password gated)
  const [frUnlocked, setFrUnlocked] = useState(false);
  const [frPassword, setFrPassword] = useState('');
  const [frPasswordInput, setFrPasswordInput] = useState('');
  const [frUnlocking, setFrUnlocking] = useState(false);
  const [frUnlockError, setFrUnlockError] = useState('');
  const todayIso = new Date().toISOString().slice(0, 10);
  const monthStartIso = `${todayIso.slice(0, 7)}-01`;
  const [frStartDate, setFrStartDate] = useState(monthStartIso);
  const [frEndDate, setFrEndDate] = useState(todayIso);
  const [frLoading, setFrLoading] = useState(false);
  const [frDownloading, setFrDownloading] = useState(false);
  const [frError, setFrError] = useState('');
  const [frReport, setFrReport] = useState(null); // { rows, totals }
  const [frPage, setFrPage] = useState(1);
  const FR_PAGE_SIZE = 25;
  const frTotalPages = Math.max(1, Math.ceil((frReport?.rows.length || 0) / FR_PAGE_SIZE));
  const frPageRows = frReport ? frReport.rows.slice((frPage - 1) * FR_PAGE_SIZE, frPage * FR_PAGE_SIZE) : [];

  // Polling
  const [nextRefreshIn, setNextRefreshIn] = useState(POLLING_INTERVAL / 1000);
  const refreshSeconds = POLLING_INTERVAL / 1000;
  const isVisible = standalone || isOpen;

  const abortRef = useRef(null);

  // ---- Financial report (Platform Fee / Marketing Discount) ----
  const handleFrUnlock = async () => {
    setFrUnlocking(true);
    setFrUnlockError('');
    try {
      const res = await fetch(`${config.customerApiBase}/ops/verify-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: frPasswordInput }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Invalid password');
      }
      setFrPassword(frPasswordInput);
      setFrPasswordInput('');
      setFrUnlocked(true);
    } catch (e) {
      setFrUnlockError(e.message || 'Invalid password');
    } finally {
      setFrUnlocking(false);
    }
  };

  const handleFrPreview = async () => {
    setFrLoading(true);
    setFrError('');
    try {
      const res = await fetch(`${config.customerApiBase}/ops/financial-report/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: frPassword, start_date: frStartDate, end_date: frEndDate }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `Request failed (${res.status})`);
      setFrReport({ rows: data.rows || [], totals: data.totals || {} });
      setFrPage(1);
    } catch (e) {
      setFrError(e.message || 'Failed to load report');
      setFrReport(null);
    } finally {
      setFrLoading(false);
    }
  };

  const handleFrDownload = async () => {
    setFrDownloading(true);
    setFrError('');
    try {
      const res = await fetch(`${config.customerApiBase}/ops/financial-report/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: frPassword, start_date: frStartDate, end_date: frEndDate }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Request failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Financial_Report_${frStartDate}_to_${frEndDate}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setFrError(e.message || 'Failed to download report');
    } finally {
      setFrDownloading(false);
    }
  };

  // ---- Fetch summary ----
  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const params = new URLSearchParams({ days: periodDays });
      if (periodStartDate && periodEndDate) {
        params.set('date_from', periodStartDate);
        params.set('date_to', periodEndDate);
      }
      if (summaryCustomerId) params.set('customer_id', summaryCustomerId);
      if (viewMode === 'b2b') params.set('source', 'b2b');
      else if (viewMode === 'whatsapp') params.set('source', 'whatsapp');
      const res = await fetch(`${PAYMENT_API}/dashboard/summary?${params}`);
      if (!res.ok) throw new Error(`Payment summary request failed (${res.status})`);
      const data = await res.json();
      if (!data.success) throw new Error(data.detail || 'Payment summary request failed');
      setSummary(data);
      setPaymentBackendDown(false);
    } catch (e) {
      console.error('Failed to fetch payment summary', e);
      setPaymentBackendDown(true);
    } finally {
      setSummaryLoading(false);
    }
  }, [periodDays, periodStartDate, periodEndDate, summaryCustomerId, viewMode]);

  const fetchPaymentSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const res = await fetch(`${PAYMENT_API}/settings`);
      const data = await res.json();
      if (data.success && data.settings) {
        setAutoSendSettings((prev) => ({ ...prev, ...data.settings }));
        setPaymentBackendDown(false);
      }
    } catch (e) {
      console.error('Failed to fetch payment settings', e);
      setPaymentBackendDown(true);
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  const handleAutoSendToggle = async (key) => {
    const nextValue = !autoSendSettings[key];
    setSettingsSavingKey(key);
    try {
      const res = await fetch(`${PAYMENT_API}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [key]: nextValue }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.detail || 'Failed to update setting');
      }
      setAutoSendSettings((prev) => ({ ...prev, ...data.settings }));
    } catch (e) {
      console.error('Failed to update auto-send setting', e);
      alert(e.message || 'Failed to update setting');
    } finally {
      setSettingsSavingKey('');
    }
  };

  // ---- Fetch transactions ----
  const fetchTransactions = useCallback(async (page = 1) => {
    if (paymentBackendDown) {
      setTxnLoading(false);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    setTxnLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 20 });
      if (filterStatus) params.set('status', filterStatus);
      if (filterCustomerId) params.set('customer_id', filterCustomerId);
      if (filterDateFrom) params.set('date_from', filterDateFrom);
      if (filterDateTo) params.set('date_to', filterDateTo);
      if (viewMode === 'b2b') params.set('source', 'b2b');
      else if (viewMode === 'whatsapp') params.set('source', 'whatsapp');

      const res = await fetch(`${PAYMENT_API}/dashboard/transactions?${params}`, {
        signal: abortRef.current.signal,
      });
      const data = await res.json();
      if (data.success) {
        setTransactions(data.transactions);
        setTxnPage(data.page);
        setTxnTotalPages(data.total_pages);
        setTxnTotal(data.total);
      }
    } catch (e) {
      if (e.name !== 'AbortError') console.error('Failed to fetch transactions', e);
    } finally {
      setTxnLoading(false);
    }
  }, [filterStatus, filterCustomerId, filterDateFrom, filterDateTo, viewMode, paymentBackendDown]);

  // ---- Fetch pending bookings ----
  const fetchPendingBookings = useCallback(async (page = 1) => {
    if (paymentBackendDown) {
      setPendingLoading(false);
      return;
    }
    setPendingLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 20, days: periodDays });
      if (periodStartDate && periodEndDate) {
        params.set('date_from', periodStartDate);
        params.set('date_to', periodEndDate);
      }
      if (pendingFilterCustomerId) params.set('customer_id', String(pendingFilterCustomerId));
      if (viewMode === 'b2b') params.set('source', 'b2b');
      else if (viewMode === 'whatsapp') params.set('source', 'whatsapp');
      const res = await fetch(`${PAYMENT_API}/dashboard/pending-bookings?${params}`);
      const data = await res.json();
      if (data.success) {
        setPendingBookings(data.bookings);
        setPendingPage(data.page);
        setPendingTotalPages(data.total_pages);
        setPendingTotal(data.total);
      }
    } catch (e) {
      console.error('Failed to fetch pending bookings', e);
    } finally {
      setPendingLoading(false);
    }
  }, [periodDays, periodStartDate, periodEndDate, pendingFilterCustomerId, paymentBackendDown, viewMode]);

  // ---- Fetch Driver Transactions ----
  const fetchDriverTransactions = useCallback(async (page = 1) => {
    const OPS_API = config.opsApiBase;
    setDriverTxnsLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 20 });
      if (driverTxnFilterStatus) params.set('status', driverTxnFilterStatus);
      if (driverTxnFilterDateFrom) params.set('date_from', driverTxnFilterDateFrom);
      if (driverTxnFilterDateTo) params.set('date_to', driverTxnFilterDateTo);
      if (driverTxnSearch) params.set('search', driverTxnSearch);

      const res = await fetch(`${OPS_API}/driver-transactions?${params}`);
      const data = await res.json();
      if (data.success) {
        setDriverTxns(data.transactions);
        setDriverTxnPage(data.page);
        setDriverTxnTotalPages(data.total_pages);
        setDriverTxnTotal(data.total);
      }
    } catch (e) {
      console.error('Failed to fetch driver transactions', e);
    } finally {
      setDriverTxnsLoading(false);
    }
  }, [driverTxnFilterStatus, driverTxnFilterDateFrom, driverTxnFilterDateTo, driverTxnSearch]);

  const fetchCashPaidDrivers = useCallback(async (page = 1) => {
    const OPS_API = config.opsApiBase;
    setCashPaidLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 20 });
      if (driverTxnFilterDateFrom) params.set('date_from', driverTxnFilterDateFrom);
      if (driverTxnFilterDateTo) params.set('date_to', driverTxnFilterDateTo);
      if (driverTxnSearch) params.set('search', driverTxnSearch);

      const res = await fetch(`${OPS_API}/payments/cash-payments/driver-summary?${params}`);
      const data = await res.json();
      if (data.success) {
        setCashPaidDrivers(data.drivers || []);
        setCashPaidPage(data.page || 1);
        setCashPaidTotalPages(data.total_pages || 1);
        setCashPaidTotal(data.total || 0);
      }
    } catch (e) {
      console.error('Failed to fetch cash paid drivers', e);
    } finally {
      setCashPaidLoading(false);
    }
  }, [driverTxnFilterDateFrom, driverTxnFilterDateTo, driverTxnSearch]);

  // ---- Fetch pricing rows ----
  const fetchPricing = useCallback(async (page = 1) => {
    setPricingLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 20 });
      const citiesForFilter = selectedCities?.length ? selectedCities : (selectedCity ? [selectedCity] : []);

      if (citiesForFilter.length) {
        params.set('cities', citiesForFilter.join(','));
      }
      if (pricingClassFilter) params.set('class_id', pricingClassFilter);
      if (pricingPayFilter) params.set('pay_view', pricingPayFilter);

      const res = await fetch(`${PRICING_API}/pricing?${params.toString()}`);
      const data = await res.json();
      if (data.success) {
        setPricingRows(data.pricing || []);
        setPricingPage(data.page || 1);
        setPricingTotalPages(data.total_pages || 1);
        setPricingTotal(data.total || 0);
      }
    } catch (e) {
      console.error('Failed to fetch pricing rows', e);
    } finally {
      setPricingLoading(false);
    }
  }, [selectedCities, selectedCity, pricingClassFilter, pricingPayFilter]);

  const handlePricingFieldChange = (rowId, field, value) => {
    setPricingEdits((prev) => ({
      ...prev,
      [rowId]: {
        ...(prev[rowId] || {}),
        [field]: value,
      },
    }));
  };

  const handleSavePricingRow = useCallback(async (row) => {
    const draft = pricingEdits[row.id] || {};
    const payload = {};

    Object.entries(draft).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) {
        payload[k] = Number(v);
      }
    });

    if (Object.keys(payload).length === 0) {
      alert('No edits to save for this row.');
      return;
    }

    setPricingSavingId(row.id);
    try {
      const res = await fetch(`${PRICING_API}/pricing/${row.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.detail || data.error || `HTTP ${res.status}`);
      }

      setPricingRows((prev) => prev.map((r) => (r.id === row.id ? data.pricing : r)));
      setPricingEdits((prev) => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
      alert(`Pricing row #${row.id} updated successfully.`);
    } catch (e) {
      alert(`Failed to update pricing row #${row.id}: ${e.message}`);
    } finally {
      setPricingSavingId(null);
    }
  }, [pricingEdits]);

  // ---- Sync payment status from Cashfree (for missed webhooks) ----
  const [syncingFor, setSyncingFor] = React.useState(null);
  const handleSyncPayment = useCallback(async (bookingId) => {
    if (syncingFor) return;
    setSyncingFor(bookingId);
    try {
      const res = await fetch(`${PAYMENT_API}/reconcile/${bookingId}`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      if (data.status === 'PAID') {
        alert(`Payment confirmed! Transaction ID: ${data.transaction_id}`);
        fetchTransactions(txnPage);
        fetchSummary();
      } else if (data.status === 'EXPIRED') {
        alert('This payment link has expired. You can now generate a new link for this booking.');
        fetchTransactions(txnPage);
        fetchPendingBookings(pendingPage);
        fetchSummary();
      } else {
        alert('No payment found on Cashfree yet for this booking.');
      }
    } catch (e) {
      alert(`Sync failed: ${e.message}`);
    } finally {
      setSyncingFor(null);
    }
  }, [syncingFor, fetchTransactions, txnPage, fetchSummary, fetchPendingBookings, pendingPage]);

  // ---- Send payment link / QR ----
  const handleSendPaymentLink = useCallback(async (booking, deliveryMode = 'link') => {
    if (sendingLinkFor) return;
    setSendingLinkFor(booking.booking_id);
    try {
      const res = await fetch(`${PAYMENT_API}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_id: booking.booking_id,
          amount: booking.payment_estimate,
          customer_phone: booking.customer_phone,
          customer_name: booking.customer_name,
          delivery_mode: deliveryMode || 'link',
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.detail || data.error || `HTTP ${res.status}`);
      }
      const label = (data.instrument_type === 'QR_CODE' || deliveryMode === 'qr') ? 'QR' : 'Payment link';
      if (data.is_existing) {
        alert(`Booking #${booking.booking_id} already has an active ${label} — reusing existing. Cancel it first to send a new one.`);
      } else {
        alert(`${label} sent for Booking #${booking.booking_id}.`);
      }
      fetchPendingBookings(pendingPage);
      fetchSummary();
    } catch (e) {
      console.error(e);
      alert(e.message || 'Network error while generating payment');
    } finally {
      setSendingLinkFor(null);
    }
  }, [sendingLinkFor, fetchPendingBookings, pendingPage, fetchSummary]);

  const handleCancelPaymentInstrument = useCallback(async (bookingOrTxn, instrumentType) => {
    const bookingId = bookingOrTxn.booking_id || (Array.isArray(bookingOrTxn.booking_ids) ? bookingOrTxn.booking_ids[0] : null);
    const transactionId = bookingOrTxn.id || bookingOrTxn.transaction_id || null;
    if (!bookingId && !transactionId) return;
    const typeLabel = instrumentType === 'QR_CODE' ? 'QR code' : instrumentType === 'PAYMENT_LINK' ? 'payment link' : 'payment link/QR';
    if (!window.confirm(`Cancel this active ${typeLabel}?`)) return;
    try {
      const body = transactionId
        ? { transaction_id: String(transactionId) }
        : { booking_id: bookingId };
      if (instrumentType && !transactionId) {
        body.instrument_type = instrumentType;
      }
      const res = await fetch(`${PAYMENT_API}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || data.error || `HTTP ${res.status}`);
      }
      const count = (data.cancelled || []).length;
      alert(count ? `Cancelled ${count} instrument(s).` : 'Nothing cancelled.');
      fetchPendingBookings(pendingPage);
      fetchTransactions(txnPage);
      fetchSummary();
    } catch (e) {
      alert(`Cancel failed: ${e.message}`);
    }
  }, [fetchPendingBookings, pendingPage, fetchTransactions, txnPage, fetchSummary]);

  const handleBulkPayment = async (deliveryMode = 'link') => {
    if (selectedPendingBookings.size < 2) return;
    setGeneratingBulk(true);
    setBulkLinkResult(null);
    try {
      const ids = Array.from(selectedPendingBookings);
      const res = await fetch(`${PAYMENT_API}/bulk-generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_ids: ids,
          source: 'ops',
          delivery_mode: deliveryMode,
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        const instrumentType = data.instrument_type || 'PAYMENT_LINK';
        setBulkLinkResult({
          link: data.payment_url || data.payment_link,
          amount: data.amount,
          count: ids.length,
          instrumentType,
          label: instrumentType === 'QR_CODE' ? 'QR' : 'Payment Link',
        });
        setSelectedPendingBookings(new Set());
        fetchPendingBookings(pendingPage);
        fetchSummary();
      } else {
        alert(data.detail || data.error || `Failed to generate bulk ${deliveryMode === 'qr' ? 'QR' : 'link'}`);
      }
    } catch (e) {
      console.error(e);
      alert(`Network error while generating bulk payment ${deliveryMode === 'qr' ? 'QR' : 'link'}`);
    } finally {
      setGeneratingBulk(false);
    }
  };

  const toggleSelectPending = (id) => {
    setSelectedPendingBookings(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAllPending = () => {
    const valid = pendingBookings.filter(b => !b.has_active_link);
    if (valid.every(b => selectedPendingBookings.has(b.booking_id))) {
      setSelectedPendingBookings(new Set());
    } else {
      setSelectedPendingBookings(new Set(valid.map(b => b.booking_id)));
    }
  };

  const openManualPaymentModal = useCallback((booking) => {
    setManualPayForm({
      mode: 'CASH',
      otherProvider: '',
      transactionTime: toLocalDateTimeInput(),
      gatewayTxnId: '',
      amount: String(Number(booking.payment_estimate || 0).toFixed(2)),
      notes: '',
    });
    setManualPaymentModal(booking);
  }, []);

  const handleRecordManualPayment = useCallback(async () => {
    if (!manualPaymentModal || manualPaySaving) return;

    const amount = Number(manualPayForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      alert('Please enter a valid amount greater than 0.');
      return;
    }

    if (manualPayForm.mode === 'OTHER' && !manualPayForm.otherProvider.trim()) {
      alert('Please enter provider name when mode is Other.');
      return;
    }

    setManualPaySaving(true);
    try {
      const res = await fetch(`${PAYMENT_API}/manual-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_id: manualPaymentModal.booking_id,
          amount,
          mode: manualPayForm.mode,
          other_provider: manualPayForm.mode === 'OTHER' ? manualPayForm.otherProvider.trim() : null,
          transaction_time: manualPayForm.transactionTime
            ? new Date(manualPayForm.transactionTime).toISOString()
            : null,
          gateway_transaction_id: manualPayForm.gatewayTxnId.trim() || null,
          notes: manualPayForm.notes.trim() || null,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.detail || data.error || `HTTP ${res.status}`);
      }

      const warn = data.superseded_instruments?.failed?.length ? `\n\n${data.message}` : '';
      alert(`Manual payment recorded for Booking #${manualPaymentModal.booking_id}.${warn}`);
      setManualPaymentModal(null);
      fetchPendingBookings(pendingPage);
      fetchSummary();
      if (activeTab === 'transactions') fetchTransactions(txnPage);
    } catch (e) {
      alert(`Failed to record manual payment: ${e.message}`);
    } finally {
      setManualPaySaving(false);
    }
  }, [manualPaymentModal, manualPaySaving, manualPayForm, fetchPendingBookings, pendingPage, fetchSummary, activeTab, fetchTransactions, txnPage]);

  // ---- Polling: fetch data on interval ----
  useEffect(() => {
    if (!isVisible) return;
    fetchSummary();
    fetchPaymentSettings();
  }, [isVisible, fetchSummary, fetchPaymentSettings]);

  useEffect(() => {
    if (!isVisible) return;
    if (activeTab === 'transactions' && !paymentBackendDown) fetchTransactions(1);
  }, [isVisible, activeTab, fetchTransactions, paymentBackendDown]);

  useEffect(() => {
    if (!isVisible) return;
    if (activeTab === 'pending' && !paymentBackendDown) fetchPendingBookings(1);
  }, [isVisible, activeTab, fetchPendingBookings, paymentBackendDown]);

  useEffect(() => {
    if (!isVisible) return;
    if (activeTab === 'pricing') { setPricingPage(1); fetchPricing(1); }
  }, [isVisible, activeTab, fetchPricing]);

  useEffect(() => {
    if (!isVisible) return;
    if (activeTab === 'driver_txns') {
      if (driverTxnView === 'cash_paid') {
        fetchCashPaidDrivers(1);
      } else {
        fetchDriverTransactions(1);
      }
    }
  }, [isVisible, activeTab, driverTxnView, fetchDriverTransactions, fetchCashPaidDrivers]);

  // Auto-refresh polling
  useEffect(() => {
    if (!isVisible) return;

    const interval = setInterval(() => {
      if (!paymentBackendDown) {
        fetchSummary();
        if (activeTab === 'transactions') fetchTransactions(txnPage);
        if (activeTab === 'pending') fetchPendingBookings(pendingPage);
      }
      if (activeTab === 'pricing') fetchPricing(pricingPage);
    }, POLLING_INTERVAL);

    return () => clearInterval(interval);
  }, [isVisible, activeTab, fetchSummary, fetchTransactions, fetchPendingBookings, fetchPricing, txnPage, pendingPage, pricingPage, paymentBackendDown]);

  // Reset refresh countdown when summary data arrives
  useEffect(() => {
    if (summary) setNextRefreshIn(refreshSeconds);
  }, [summary, refreshSeconds]);

  // Tick down countdown
  useEffect(() => {
    if (!isVisible) return;
    const timer = setInterval(() => {
      setNextRefreshIn((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [isVisible]);

  // Sync pageInput states with actual page states
  useEffect(() => { setTxnPageInput(txnPage); }, [txnPage]);
  useEffect(() => { setPendingPageInput(pendingPage); }, [pendingPage]);
  useEffect(() => { setPricingPageInput(pricingPage); }, [pricingPage]);
  useEffect(() => { setDriverTxnPageInput(driverTxnPage); }, [driverTxnPage]);
  useEffect(() => { setCashPaidPageInput(cashPaidPage); }, [cashPaidPage]);

  // ---- Page input handlers ----
  // Transactions
  const handleTxnPageInputChange = (e) => { setTxnPageInput(e.target.value); };
  const handleTxnPageInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      const num = parseInt(txnPageInput, 10);
      if (Number.isFinite(num) && num >= 1 && num <= txnTotalPages) {
        fetchTransactions(num);
      } else {
        setTxnPageInput(txnPage);
      }
    }
  };
  const handleTxnPageInputBlur = () => { setTxnPageInput(txnPage); };

  // Pending
  const handlePendingPageInputChange = (e) => { setPendingPageInput(e.target.value); };
  const handlePendingPageInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      const num = parseInt(pendingPageInput, 10);
      if (Number.isFinite(num) && num >= 1 && num <= pendingTotalPages) {
        fetchPendingBookings(num);
      } else {
        setPendingPageInput(pendingPage);
      }
    }
  };
  const handlePendingPageInputBlur = () => { setPendingPageInput(pendingPage); };

  // Pricing
  const handlePricingPageInputChange = (e) => { setPricingPageInput(e.target.value); };
  const handlePricingPageInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      const num = parseInt(pricingPageInput, 10);
      if (Number.isFinite(num) && num >= 1 && num <= pricingTotalPages) {
        setPricingPage(num);
        fetchPricing(num);
      } else {
        setPricingPageInput(pricingPage);
      }
    }
  };
  const handlePricingPageInputBlur = () => { setPricingPageInput(pricingPage); };

  // Driver Transactions (payouts)
  const handleDriverTxnPageInputChange = (e) => { setDriverTxnPageInput(e.target.value); };
  const handleDriverTxnPageInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      const num = parseInt(driverTxnPageInput, 10);
      if (Number.isFinite(num) && num >= 1 && num <= driverTxnTotalPages) {
        fetchDriverTransactions(num);
      } else {
        setDriverTxnPageInput(driverTxnPage);
      }
    }
  };
  const handleDriverTxnPageInputBlur = () => { setDriverTxnPageInput(driverTxnPage); };

  // Cash Paid Drivers
  const handleCashPaidPageInputChange = (e) => { setCashPaidPageInput(e.target.value); };
  const handleCashPaidPageInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      const num = parseInt(cashPaidPageInput, 10);
      if (Number.isFinite(num) && num >= 1 && num <= cashPaidTotalPages) {
        fetchCashPaidDrivers(num);
      } else {
        setCashPaidPageInput(cashPaidPage);
      }
    }
  };
  const handleCashPaidPageInputBlur = () => { setCashPaidPageInput(cashPaidPage); };

  // ---- Derived metrics ----
  const overview = summary?.overview || {};

  // ---- Period handlers ----
  const handlePresetPeriod = (d) => {
    setPeriodDays(d);
    setCustomDays('');
  };

  const handleCustomDaysApply = () => {
    const v = parseInt(customDays, 10);
    if (v >= 1 && v <= 365) {
      setPeriodDays(v);
    }
  };

  const handleCustomDaysKeyDown = (e) => {
    if (e.key === 'Enter') handleCustomDaysApply();
  };

  // ---- Event Handlers ----
  const handleTxnPageChange = (dir) => {
    const next = txnPage + dir;
    if (next >= 1 && next <= txnTotalPages) fetchTransactions(next);
  };

  const handlePendingPageChange = (dir) => {
    const next = pendingPage + dir;
    if (next >= 1 && next <= pendingTotalPages) fetchPendingBookings(next);
  };

  const handleFilterApply = () => fetchTransactions(1);

  const handleFilterReset = () => {
    setFilterStatus('');
    setFilterCustomerId('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterDateTo('');
  };

  const handlePendingCustomerClick = (custId, custName) => {
    setPendingFilterCustomerId(custId);
    setPendingFilterCustomerName(custName || '');
    setActiveTab('pending');
  };

  const jumpToTransactions = (status = '', onlyToday = false) => {
    const todayStr = getTodayStr();
    setFilterStatus(status);
    setFilterCustomerId('');
    setFilterCustomerId('');
    setFilterDateFrom(onlyToday ? todayStr : '');
    setFilterDateTo(onlyToday ? todayStr : '');
    setActiveTab('transactions');
  };

  const handleManualRefresh = () => {
    fetchSummary();
    fetchPaymentSettings();
    if (activeTab === 'transactions' && !paymentBackendDown) fetchTransactions(txnPage);
    if (activeTab === 'pending' && !paymentBackendDown) fetchPendingBookings(pendingPage);
    if (activeTab === 'pricing') fetchPricing(pricingPage);
    if (activeTab === 'driver_txns') {
      if (driverTxnView === 'cash_paid') {
        fetchCashPaidDrivers(cashPaidPage);
      } else {
        fetchDriverTransactions(driverTxnPage);
      }
    }
    setNextRefreshIn(refreshSeconds);
  };

  const handleViewBooking = useCallback(async (txn, primaryBookingId) => {
    setSelectedTxn(txn);
    setHistoryLoading(true);
    setBookingHistory([]);
    const bid = primaryBookingId || txn.booking_ids?.[0] || txn.booking_id;
    if (!bid) {
      setHistoryLoading(false);
      return;
    }
    try {
      const res = await fetch(`${PAYMENT_API}/dashboard/booking-transactions/${bid}`);
      const data = await res.json();
      if (data.success) setBookingHistory(data.transactions);
    } catch (e) {
      console.error('Failed to fetch booking history', e);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const renderTransactionRow = (txn) => {
    const bookingIds = txn.booking_ids || [txn.booking_id].filter(Boolean);
    const isBulk = bookingIds.length > 1;
    const primaryId = bookingIds[0];
    const sourceLabel = displaySource(txn.source);

    return (
      <tr key={txn.id}>
        <td className={s.monoCell}>
          {isBulk ? (
            <div style={{ marginBottom: 6 }}>
              <span
                className={s.attemptsBadge}
                style={{
                  background: '#fef3c7',
                  color: '#92400e',
                  borderColor: '#fcd34d',
                  fontSize: '0.85rem',
                  padding: '4px 10px',
                  fontWeight: 700,
                }}
              >
                Bulk Payment
              </span>
              <div
                className={s.bulkIdList}
                title={bookingIds.join(', ')}
                style={{
                  fontSize: '0.7rem',
                  color: '#6b7280',
                  marginTop: 4,
                  maxWidth: '130px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {bookingIds.map((id) => `#${id}`).join(', ')}
              </div>
            </div>
          ) : (
            <>#{primaryId}</>
          )}
          {txn.internal_reference && (
            <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: 2 }} title={txn.internal_reference}>
              {txn.internal_reference}
            </div>
          )}
        </td>
        <td>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.75rem' }}>
            <span
              className={s.attemptsBadge}
              style={{
                alignSelf: 'flex-start',
                background: sourceLabel === 'b2b' ? '#eef2ff' : '#f3f4f6',
                color: sourceLabel === 'b2b' ? '#4338ca' : '#4b5563',
              }}
            >
              {sourceLabel.toUpperCase()}
            </span>
            {txn.business_org_name && (
              <span style={{ color: '#6b7280', fontSize: '0.7rem' }} title="B2B account">
                {txn.business_org_name}
              </span>
            )}
          </div>
        </td>
        <td>
          <div className={s.custCell}>
            <span className={s.custName}>{txn.business_org_name || txn.customer_name}</span>
            <span className={s.custPhone}>{txn.customer_phone}</span>
          </div>
        </td>
        <td className={s.amountCell}>
          <div>{currency(txn.amount)}</div>
          {renderInstallmentMeta(txn)}
        </td>
        <td style={{ fontSize: '0.75rem' }}>
          {instrumentLabel(txn.instrument_type)}
        </td>
        <td>
          <span className={`${s.txnStatus} ${s[statusColor(txn.status)]}`}>
            {txn.status}
          </span>
        </td>
        <td className={s.dateCell}>{fmtDate(txn.created_at)}</td>
        <td className={s.dateCell}>{txn.paid_at ? fmtDateTime(txn.paid_at) : '—'}</td>
        <td>
          <button
            className={s.viewBtn}
            onClick={() => handleViewBooking(txn, primaryId)}
          >
            <i className="fa fa-eye" /> View
          </button>
          {txn.status === 'ACTIVE' && (
            <>
              <button
                className={s.syncBtn}
                onClick={() => handleSyncPayment(primaryId)}
                disabled={syncingFor === primaryId}
                title="Check provider for missed payment"
              >
                {syncingFor === primaryId
                  ? <i className="fa fa-spinner fa-spin" />
                  : <i className="fa fa-refresh" />}
                {' '}Sync
              </button>
              <button
                className={s.syncBtn}
                onClick={() => handleCancelPaymentInstrument(txn, txn.instrument_type)}
                title={`Cancel this active ${instrumentLabel(txn.instrument_type)}`}
                style={{ marginLeft: 6, color: '#dc2626' }}
              >
                <i className="fa fa-ban" /> Cancel
              </button>
            </>
          )}
        </td>
      </tr>
    );
  };

  if (!standalone && !isOpen) return null;

  return (
    <div
      className={standalone ? s.pageRoot : s.overlay}
      onClick={standalone ? undefined : onClose}
    >
      <div
        className={`${s.modal} ${standalone ? s.modalStandalone : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={s.modalHeader}>
          <div className={s.modalHeaderLeft}>
            <div className={s.modalIcon}>
              <i className="fa fa-credit-card" />
            </div>
            <div>
              <h2 className={s.modalTitle}>Payment Control Center</h2>
              <p className={s.modalSubtitle}>Monitor payments, collections &amp; outstanding dues</p>
            </div>
          </div>
          <div className={s.modalHeaderRight}>
            {/* All / WhatsApp / B2B Toggle */}
            <div style={{ display: 'flex', gap: '2px', background: 'rgba(0,0,0,0.06)', borderRadius: '10px', padding: '3px', marginRight: '12px' }}>
              {[{ key: 'all', label: 'All' }, { key: 'whatsapp', label: 'WhatsApp' }, { key: 'b2b', label: 'B2B' }].map(m => (
                <button
                  key={m.key}
                  onClick={() => setViewMode(m.key)}
                  style={{
                    padding: '6px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                    fontSize: '12px', fontWeight: 700, transition: 'all 0.2s',
                    background: viewMode === m.key ? (m.key === 'b2b' ? '#6366f1' : m.key === 'whatsapp' ? '#22c55e' : '#fff') : 'transparent',
                    color: viewMode === m.key ? (m.key === 'all' ? '#1e293b' : '#fff') : '#94a3b8',
                    boxShadow: viewMode === m.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  }}
                >
                  {m.key === 'b2b' && <i className="fa fa-star" style={{ marginRight: '4px', fontSize: '10px' }} />}
                  {m.key === 'whatsapp' && <i className="fa fa-whatsapp" style={{ marginRight: '4px', fontSize: '11px' }} />}
                  {m.label}
                </button>
              ))}
            </div>
            <button
              className={s.refreshBtn}
              onClick={handleManualRefresh}
              title={`Auto-refreshing in ${Math.ceil(nextRefreshIn)}s`}
            >
              <i className={`fa ${summaryLoading ? 'fa-spinner fa-spin' : 'fa-refresh'}`} />
              {!summaryLoading && (
                <div className={s.refreshProgressTrack}>
                  <div
                    className={s.refreshProgressBar}
                    style={{ width: `${Math.max(0, Math.min(100, (nextRefreshIn / refreshSeconds) * 100))}%` }}
                  />
                </div>
              )}
            </button>
            {!standalone && (
              <button className={s.closeBtn} onClick={onClose}>
                <i className="fa fa-times" />
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className={s.tabBar}>
          {[
            { key: 'overview', label: 'Overview', icon: 'fa-bar-chart' },
            { key: 'transactions', label: 'All Transactions', icon: 'fa-list' },
            { key: 'pending', label: 'Pending Dues', icon: 'fa-exclamation-triangle', badge: overview.unpaid_booking_count },
            { key: 'driver_txns', label: 'Driver Transactions', icon: 'fa-car' },
            { key: 'pricing', label: 'Pricing', icon: 'fa-table' },
            { key: 'financial_report', label: 'Financial Report', icon: 'fa-lock' },
          ].map((t) => (
            <button
              key={t.key}
              className={`${s.tab} ${activeTab === t.key ? s.tabActive : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              <i className={`fa ${t.icon}`} />
              {t.label}
              {t.badge > 0 && <span className={s.tabBadge}>{t.badge}</span>}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className={s.modalBody}>
          {paymentBackendDown && activeTab !== 'pricing' && (
            <div className={s.customerFilterBanner}>
              <span>
                <i className="fa fa-exclamation-triangle" /> Payment backend is currently unreachable.
                You can still use the Pricing tab.
              </span>
            </div>
          )}

          {/* ===================== OVERVIEW TAB ===================== */}
          {activeTab === 'overview' && (
            <>
              <div style={{
                marginBottom: '16px',
                padding: '14px 16px',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                background: '#fff',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700 }}>Auto-send payment links</h3>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6b7280' }}>
                      Trip automation only. Manual send from ops stays available.
                    </p>
                  </div>
                  {settingsLoading && <i className="fa fa-spinner fa-spin" style={{ color: '#6b7280' }} />}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
                  {[
                    { key: 'auto_send_consumer_at_pickup', label: 'Consumer at pickup' },
                    { key: 'auto_send_consumer_at_drop', label: 'Consumer at drop' },
                    { key: 'auto_send_b2b_on_complete', label: 'B2B on trip complete' },
                    { key: 'auto_send_hourly_on_final_bill', label: 'Hourly on final bill' },
                  ].map(({ key, label }) => (
                    <label
                      key={key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px',
                        padding: '10px 12px',
                        borderRadius: '10px',
                        background: autoSendSettings[key] ? '#ecfdf5' : '#f8fafc',
                        border: `1px solid ${autoSendSettings[key] ? '#bbf7d0' : '#e5e7eb'}`,
                        cursor: settingsSavingKey === key ? 'wait' : 'pointer',
                      }}
                    >
                      <span style={{ fontSize: '13px', fontWeight: 600 }}>{label}</span>
                      <input
                        type="checkbox"
                        checked={Boolean(autoSendSettings[key])}
                        disabled={settingsSavingKey === key}
                        onChange={() => handleAutoSendToggle(key)}
                      />
                    </label>
                  ))}
                </div>
              </div>
              <PaymentOverview
               summary={summary}
               periodDays={periodDays}
               customDays={customDays}
               setPeriodDays={setPeriodDays}
               setCustomDays={setCustomDays}
               periodStartDate={periodStartDate}
               periodEndDate={periodEndDate}
               setPeriodStartDate={setPeriodStartDate}
               setPeriodEndDate={setPeriodEndDate}
               summaryLoading={summaryLoading}
               paymentBackendDown={paymentBackendDown}
               summaryCustomerInput={summaryCustomerInput}
               summaryCustomerId={summaryCustomerId}
               setSummaryCustomerInput={setSummaryCustomerInput}
               setSummaryCustomerId={setSummaryCustomerId}
               viewMode={viewMode}
               onManualRefresh={handleManualRefresh}
               setActiveTab={setActiveTab}
               jumpToTransactions={jumpToTransactions}
               handlePendingCustomerClick={handlePendingCustomerClick}
             />
            </>
          )}

          {/* ===================== TRANSACTIONS TAB ===================== */}
          {activeTab === 'transactions' && (
            <>
              {/* Filters */}
              <div className={s.filterRow}>
                <select
                  className={s.filterSelect}
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="PAID">Paid</option>
                  <option value="ACTIVE">Active</option>
                  <option value="EXPIRED">Expired</option>
                </select>
                <input
                  className={s.filterInput}
                  type="number"
                  placeholder="Customer ID"
                  value={filterCustomerId}
                  onChange={(e) => setFilterCustomerId(e.target.value)}
                />
                <input
                  className={s.filterInput}
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  title="From date"
                />
                <input
                  className={s.filterInput}
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  title="To date"
                />
                <button className={s.filterApplyBtn} onClick={handleFilterApply}>
                  <i className="fa fa-search" /> Search
                </button>
                <button className={s.filterResetBtn} onClick={handleFilterReset}>
                  <i className="fa fa-times" /> Reset
                </button>
              </div>

              <div className={s.txnMeta}>
                Showing {transactions.length} of {txnTotal} transactions
              </div>

              {txnLoading ? (
                <div className={s.loadingState}>
                  <div className={s.spinner} />
                  <p>Loading transactions…</p>
                </div>
              ) : transactions.length === 0 ? (
                <div className={s.emptyState}>
                  <i className="fa fa-inbox" />
                  <p>No transactions found</p>
                </div>
              ) : (
                <>
                  <div className={s.txnTableWrapper}>
                    <table className={s.txnTable}>
                      <thead>
                        <tr>
                          <th>Booking</th>
                          <th>Source</th>
                          <th>Customer</th>
                          <th>Amount</th>
                          <th>Instrument</th>
                          <th>Status</th>
                          <th>Created</th>
                          <th>Paid At</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map(renderTransactionRow)}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className={s.pagination}>
                    <button
                      className={s.pageBtn}
                      disabled={txnPage <= 1}
                      onClick={() => handleTxnPageChange(-1)}
                    >
                      <i className="fa fa-chevron-left" /> Prev
                    </button>
                    <span className={s.pageInfo}>
                      Page
                      <input
                        type="number"
                        min={1}
                        max={txnTotalPages}
                        value={txnPageInput}
                        onChange={handleTxnPageInputChange}
                        onKeyDown={handleTxnPageInputKeyDown}
                        onBlur={handleTxnPageInputBlur}
                        className={s.pageInput}
                      />
                      of {txnTotalPages}
                    </span>
                    <button
                      className={s.pageBtn}
                      disabled={txnPage >= txnTotalPages}
                      onClick={() => handleTxnPageChange(1)}
                    >
                      Next <i className="fa fa-chevron-right" />
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {/* ===================== DRIVER TRANSACTIONS TAB ===================== */}
          {activeTab === 'driver_txns' && (
            <>
              <div className={s.periodRow}>
                <span className={s.periodLabel}><i className="fa fa-exchange" /> View:</span>
                <button
                  className={`${s.periodBtn} ${driverTxnView === 'payouts' ? s.periodActive : ''}`}
                  onClick={() => setDriverTxnView('payouts')}
                >
                  Payout Transactions
                </button>
                <button
                  className={`${s.periodBtn} ${driverTxnView === 'cash_paid' ? s.periodActive : ''}`}
                  onClick={() => setDriverTxnView('cash_paid')}
                >
                  Cash Paid Trips
                </button>
              </div>

              {/* Filters */}
              <div className={s.filterRow}>
                {driverTxnView === 'payouts' && (
                  <select
                    className={s.filterSelect}
                    value={driverTxnFilterStatus}
                    onChange={(e) => setDriverTxnFilterStatus(e.target.value)}
                  >
                    <option value="">All Status</option>
                    <option value="SUCCESS">Success</option>
                    <option value="PENDING">Pending</option>
                    <option value="FAILED">Failed</option>
                    <option value="REVERSED">Reversed</option>
                    <option value="REJECTED">Rejected</option>
                  </select>
                )}
                <input
                  className={s.filterInput}
                  type="text"
                  placeholder={driverTxnView === 'cash_paid' ? 'Driver phone, name or trip ID' : 'Driver phone, name or transfer ID'}
                  value={driverTxnSearch}
                  onChange={(e) => setDriverTxnSearch(e.target.value)}
                  style={{ width: '220px' }}
                />
                <input
                  className={s.filterInput}
                  type="date"
                  value={driverTxnFilterDateFrom}
                  onChange={(e) => setDriverTxnFilterDateFrom(e.target.value)}
                  title="From date"
                />
                <input
                  className={s.filterInput}
                  type="date"
                  value={driverTxnFilterDateTo}
                  onChange={(e) => setDriverTxnFilterDateTo(e.target.value)}
                  title="To date"
                />
                <button
                  className={s.filterApplyBtn}
                  onClick={() => {
                    if (driverTxnView === 'cash_paid') {
                      fetchCashPaidDrivers(1);
                    } else {
                      fetchDriverTransactions(1);
                    }
                  }}
                >
                  <i className="fa fa-search" /> Search
                </button>
                <button className={s.filterResetBtn} onClick={() => {
                  setDriverTxnFilterStatus('');
                  setDriverTxnFilterDateFrom(getNinetyDaysAgoStr());
                  setDriverTxnFilterDateTo(getTodayStr());
                  setDriverTxnSearch('');
                  if (driverTxnView === 'cash_paid') {
                    fetchCashPaidDrivers(1);
                  } else {
                    fetchDriverTransactions(1);
                  }
                }}>
                  <i className="fa fa-times" /> Reset
                </button>
              </div>

              <div className={s.txnMeta}>
                {driverTxnView === 'cash_paid'
                  ? `Showing ${cashPaidDrivers.length} of ${cashPaidTotal} drivers with cash-paid trips`
                  : `Showing ${driverTxns.length} of ${driverTxnTotal} driver payouts`}
              </div>

              {driverTxnView === 'cash_paid' ? (
                cashPaidLoading ? (
                  <div className={s.loadingState}>
                    <div className={s.spinner} />
                    <p>Loading drivers paid via cash…</p>
                  </div>
                ) : cashPaidDrivers.length === 0 ? (
                  <div className={s.emptyState}>
                    <i className="fa fa-inbox" />
                    <p>No cash-paid driver trips found</p>
                  </div>
                ) : (
                  <>
                    <div className={s.txnTableWrapper}>
                      <table className={s.txnTable}>
                        <thead>
                          <tr>
                            <th>Driver Name</th>
                            <th>Phone</th>
                            <th>Trips Paid via Cash</th>
                            <th>Trip IDs</th>
                            <th>Date Range</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cashPaidDrivers.map((row) => (
                            <tr key={`cash-${row.driver_id || row.driver_name}`}>
                              <td><span className={s.custName}>{row.driver_name || '—'}</span></td>
                              <td className={s.custPhone}>{row.driver_phone || '—'}</td>
                              <td className={s.amountCell} style={{ fontWeight: 'bold' }}>{row.trip_count || 0}</td>
                              <td className={s.monoCell}>
                                {(row.trip_ids || []).length > 0
                                  ? row.trip_ids.map((id) => `#${id}`).join(', ')
                                  : '—'}
                              </td>
                              <td className={s.dateCell}>
                                {row.first_cash_date && row.last_cash_date
                                  ? (row.first_cash_date === row.last_cash_date
                                    ? fmtDate(row.first_cash_date)
                                    : `${fmtDate(row.first_cash_date)} → ${fmtDate(row.last_cash_date)}`)
                                  : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className={s.pagination}>
                      <button
                        className={s.pageBtn}
                        disabled={cashPaidPage <= 1}
                        onClick={() => {
                          const prev = cashPaidPage - 1;
                          if (prev >= 1) fetchCashPaidDrivers(prev);
                        }}
                      >
                        <i className="fa fa-chevron-left" /> Prev
                      </button>
                      <span className={s.pageInfo}>
                        Page
                        <input
                          type="number"
                          min={1}
                          max={cashPaidTotalPages}
                          value={cashPaidPageInput}
                          onChange={handleCashPaidPageInputChange}
                          onKeyDown={handleCashPaidPageInputKeyDown}
                          onBlur={handleCashPaidPageInputBlur}
                          className={s.pageInput}
                        />
                        of {cashPaidTotalPages}
                      </span>
                      <button
                        className={s.pageBtn}
                        disabled={cashPaidPage >= cashPaidTotalPages}
                        onClick={() => {
                          const next = cashPaidPage + 1;
                          if (next <= cashPaidTotalPages) fetchCashPaidDrivers(next);
                        }}
                      >
                        Next <i className="fa fa-chevron-right" />
                      </button>
                    </div>
                  </>
                )
              ) : driverTxnsLoading ? (
                <div className={s.loadingState}>
                  <div className={s.spinner} />
                  <p>Loading driver transactions…</p>
                </div>
              ) : driverTxns.length === 0 ? (
                <div className={s.emptyState}>
                  <i className="fa fa-inbox" />
                  <p>No driver transactions found</p>
                </div>
              ) : (
                <>
                  <div className={s.txnTableWrapper}>
                    <table className={s.txnTable}>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Transfer ID</th>
                          <th>Driver Name</th>
                          <th>UPI / VPA</th>
                          <th>Phone</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>UTR / Ref ID</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {driverTxns.map((txn) => (
                          <tr key={txn.id}>
                            <td className={s.dateCell}>{fmtDateTime(txn.created_at)}</td>
                            <td className={s.monoCell}>{txn.transfer_id}</td>
                            <td><span className={s.custName}>{txn.driver_name || '—'}</span></td>
                            <td className={s.custPhone}>{txn.vpa || '—'}</td>
                            <td className={s.custPhone}>{txn.phone}</td>
                            <td className={s.amountCell} style={{ fontWeight: 'bold' }}>{currency(txn.amount)}</td>
                            <td>
                              <span className={`${s.txnStatus} ${s[statusColor(txn.status)] || s.blue}`}>
                                {txn.status}
                              </span>
                            </td>
                            <td className={s.monoCell} style={{ fontSize: '0.8rem', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {txn.cashfree_utr || txn.cashfree_ref_id || '—'}
                            </td>
                            <td style={{ fontSize: '0.78rem', color: '#6b7280', maxWidth: '200px' }}
                              title={txn.description || ''}>
                              {txn.description || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className={s.pagination}>
                    <button
                      className={s.pageBtn}
                      disabled={driverTxnPage <= 1}
                      onClick={() => {
                        const prev = driverTxnPage - 1;
                        if (prev >= 1) fetchDriverTransactions(prev);
                      }}
                    >
                      <i className="fa fa-chevron-left" /> Prev
                    </button>
                    <span className={s.pageInfo}>
                      Page
                      <input
                        type="number"
                        min={1}
                        max={driverTxnTotalPages}
                        value={driverTxnPageInput}
                        onChange={handleDriverTxnPageInputChange}
                        onKeyDown={handleDriverTxnPageInputKeyDown}
                        onBlur={handleDriverTxnPageInputBlur}
                        className={s.pageInput}
                      />
                      of {driverTxnTotalPages}
                    </span>
                    <button
                      className={s.pageBtn}
                      disabled={driverTxnPage >= driverTxnTotalPages}
                      onClick={() => {
                        const next = driverTxnPage + 1;
                        if (next <= driverTxnTotalPages) fetchDriverTransactions(next);
                      }}
                    >
                      Next <i className="fa fa-chevron-right" />
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {/* ===================== PENDING DUES TAB ===================== */}
          {activeTab === 'pending' && (
            <>
              <PeriodSelector
                periodDays={periodDays}
                customDays={customDays}
                setCustomDays={setCustomDays}
                handlePresetPeriod={handlePresetPeriod}
                handleCustomDaysApply={handleCustomDaysApply}
                handleCustomDaysKeyDown={handleCustomDaysKeyDown}
              />

              {pendingFilterCustomerId && (
                <div className={s.customerFilterBanner}>
                  <span>
                    <i className="fa fa-filter" /> Showing unpaid bookings for:{' '}
                    <strong>{pendingFilterCustomerName || `Customer #${pendingFilterCustomerId}`}</strong>
                  </span>
                  <button
                    className={s.customerFilterClear}
                    onClick={() => { setPendingFilterCustomerId(null); setPendingFilterCustomerName(''); }}
                  >
                    <i className="fa fa-times" /> Clear filter
                  </button>
                </div>
              )}

              <div className={s.pendingHeader}>
                <p className={s.pendingDescription}>
                  <i className="fa fa-info-circle" /> Showing completed bookings where payment has not been received.
                  You can send a payment link or QR directly from here.
                </p>
              </div>

              {pendingLoading ? (
                <div className={s.loadingState}>
                  <div className={s.spinner} />
                  <p>Loading pending bookings…</p>
                </div>
              ) : pendingBookings.length === 0 ? (
                <div className={s.emptyState}>
                  <i className="fa fa-check-circle" style={{ color: '#10b981' }} />
                  <p>No pending dues — all payments collected! 🎉</p>
                </div>
              ) : (
                <>
                  <div className={s.txnMeta} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Showing {pendingBookings.length} of {pendingTotal} unpaid bookings</span>
                    
                    {selectedPendingBookings.size > 0 && (
                      <div className={s.bulkActionBar}>
                        <span style={{ marginRight: '10px', fontSize: '13px', fontWeight: 'bold' }}>
                          {selectedPendingBookings.size} selected
                        </span>
                        <button
                          className={s.sendLinkBtn}
                          onClick={() => handleBulkPayment('link')}
                          disabled={generatingBulk || selectedPendingBookings.size < 2}
                          title={selectedPendingBookings.size < 2 ? "Select at least 2 bookings" : "Generate one link for all selected bookings"}
                          style={{ padding: '6px 12px', background: generatingBulk || selectedPendingBookings.size < 2 ? '#d1d5db' : '#4f46e5', color: '#fff', border: 'none', borderRadius: '6px', cursor: generatingBulk || selectedPendingBookings.size < 2 ? 'not-allowed' : 'pointer' }}
                        >
                          {generatingBulk ? 'Generating...' : 'Generate Bulk Link'}
                        </button>
                        <button
                          className={s.sendLinkBtn}
                          onClick={() => handleBulkPayment('qr')}
                          disabled={generatingBulk || selectedPendingBookings.size < 2}
                          title={selectedPendingBookings.size < 2 ? "Select at least 2 bookings" : "Generate one QR for all selected bookings"}
                          style={{ padding: '6px 12px', background: generatingBulk || selectedPendingBookings.size < 2 ? '#d1d5db' : '#0f766e', color: '#fff', border: 'none', borderRadius: '6px', cursor: generatingBulk || selectedPendingBookings.size < 2 ? 'not-allowed' : 'pointer' }}
                        >
                          <i className="fa fa-qrcode" /> {generatingBulk ? 'Generating...' : 'Generate Bulk QR'}
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {bulkLinkResult && (
                    <div style={{ background: '#ecfdf5', border: '1px solid #10b981', padding: '12px', borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <strong style={{ color: '#047857' }}>Bulk {bulkLinkResult.label || 'Payment Link'} Generated ({bulkLinkResult.count} bookings) — {currency(bulkLinkResult.amount)}</strong>
                        {bulkLinkResult.instrumentType === 'QR_CODE' && (
                          <div style={{ marginTop: '10px' }}>
                            <img
                              src={bulkLinkResult.link}
                              alt="Bulk payment QR"
                              style={{ width: '180px', height: '180px', objectFit: 'contain', background: '#fff', border: '1px solid #d1fae5', borderRadius: '6px' }}
                            />
                          </div>
                        )}
                        <div style={{ fontSize: '12px', fontFamily: 'monospace', color: '#065f46', marginTop: '4px' }}>{bulkLinkResult.link}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => navigator.clipboard.writeText(bulkLinkResult.link)} style={{ background: '#fff', border: '1px solid #10b981', color: '#047857', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Copy URL</button>
                        <a href={bulkLinkResult.link} target="_blank" rel="noreferrer" style={{ background: '#10b981', color: '#fff', padding: '4px 12px', borderRadius: '4px', textDecoration: 'none', fontSize: '12px' }}>Open →</a>
                      </div>
                    </div>
                  )}

                  <div className={s.pendingTableWrapper}>
                    <table className={s.pendingTable}>
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }}>
                            <input
                              type="checkbox"
                              className={s.bulkCheckbox}
                              checked={pendingBookings.length > 0 && pendingBookings.filter(b => !b.has_active_link).every(b => selectedPendingBookings.has(b.booking_id))}
                              onChange={toggleAllPending}
                            />
                          </th>
                          <th>Booking ID</th>
                          <th>Customer</th>
                          <th>Type</th>
                          <th>Amount Due</th>
                          <th>Completed</th>
                          <th>Payment Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingBookings.map((bk) => (
                          <tr key={bk.booking_id} className={selectedPendingBookings.has(bk.booking_id) ? s.rowSelected : ''}>
                            <td>
                              {!bk.has_active_link ? (
                                <input
                                  type="checkbox"
                                  className={s.bulkCheckbox}
                                  checked={selectedPendingBookings.has(bk.booking_id)}
                                  onChange={() => toggleSelectPending(bk.booking_id)}
                                />
                              ) : null}
                            </td>
                            <td className={s.monoCell}>
                              #{bk.booking_id}
                              {bk.internal_reference && (
                                <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: 2 }} title={bk.internal_reference}>
                                  {bk.internal_reference}
                                </div>
                              )}
                            </td>
                            <td>
                              <div className={s.custCell}>
                                <span className={s.custName}>{bk.business_org_name || bk.customer_name}</span>
                                <span className={s.custPhone}>{bk.customer_phone}</span>
                              </div>
                            </td>
                            <td>
                              <span className={s.bookingTypeBadge}>
                                {getDisplayBookingType(bk.booking_type, bk.source === 'b2b' || bk.internal_reference)}
                              </span>
                            </td>
                            <td className={`${s.amountCell} ${bk.payment_status === 'partial' ? '' : s.red}`} style={bk.payment_status === 'partial' ? { color: '#92400e' } : undefined}>
                              {bk.payment_status === 'partial' ? (
                                <>
                                  <div>{currency(bk.outstanding ?? bk.amount)} due</div>
                                  <div style={{ fontSize: '0.72rem', color: '#92400e' }}>
                                    Paid {currency(bk.amount_paid)} of {currency(bk.amount)}
                                  </div>
                                </>
                              ) : (
                                currency(bk.payment_estimate ?? bk.amount)
                              )}
                            </td>
                            <td className={s.dateCell}>{fmtDateTime(bk.completed_at)}</td>
                            <td>
                              {(() => {
                                const types = bk.active_instrument_types || [];
                                const hasLink = types.includes('PAYMENT_LINK');
                                const hasQr = types.includes('QR_CODE');
                                if (hasLink && hasQr) {
                                  return <span className={`${s.txnStatus} ${s.active}`}>Link & QR Active</span>;
                                } else if (hasQr) {
                                  return <span className={`${s.txnStatus} ${s.active}`} style={{ background: '#ccfbf1', color: '#0f766e' }}>QR Active</span>;
                                } else if (hasLink) {
                                  return <span className={`${s.txnStatus} ${s.active}`}>Link Active</span>;
                                } else {
                                  return <span className={`${s.txnStatus} ${s.default}`}>No Active</span>;
                                }
                              })()}
                            </td>
                            <td>
                              <div className={s.actionsCell}>
                                {(() => {
                                  const types = bk.active_instrument_types || [];
                                  const hasLink = types.includes('PAYMENT_LINK');
                                  const hasQr = types.includes('QR_CODE');
                                  return (
                                    <>
                                      <button
                                        className={s.sendLinkBtn}
                                        onClick={() => handleSendPaymentLink(bk, 'link')}
                                        disabled={sendingLinkFor === bk.booking_id || hasQr}
                                        title={hasQr ? 'Cancel the active QR first' : hasLink ? 'Resend / reuse active link' : 'Generate & send payment link'}
                                      >
                                        {sendingLinkFor === bk.booking_id ? (
                                          <><i className="fa fa-spinner fa-spin" /> Sending…</>
                                        ) : (
                                          <><i className="fa fa-paper-plane" /> {hasLink ? 'Resend Link' : 'Send Link'}</>
                                        )}
                                      </button>
                                      <button
                                        className={s.sendLinkBtn}
                                        onClick={() => handleSendPaymentLink(bk, 'qr')}
                                        disabled={sendingLinkFor === bk.booking_id || hasLink}
                                        title={hasLink ? 'Cancel the active link first' : 'Generate & send Razorpay UPI QR'}
                                        style={{ background: (hasLink || sendingLinkFor === bk.booking_id) ? undefined : '#0f766e' }}
                                      >
                                        <i className="fa fa-qrcode" /> {hasQr ? 'Resend QR' : 'Send QR'}
                                      </button>
                                      {hasLink && (
                                        <button
                                          className={s.manualPaidBtn}
                                          onClick={() => handleCancelPaymentInstrument(bk, 'PAYMENT_LINK')}
                                          title="Cancel active payment link"
                                          style={{ background: '#fff', color: '#dc2626', border: '1px solid #fecaca' }}
                                        >
                                          <i className="fa fa-ban" /> Cancel Link
                                        </button>
                                      )}
                                      {hasQr && (
                                        <button
                                          className={s.manualPaidBtn}
                                          onClick={() => handleCancelPaymentInstrument(bk, 'QR_CODE')}
                                          title="Cancel active QR code"
                                          style={{ background: '#fff', color: '#dc2626', border: '1px solid #fecaca' }}
                                        >
                                          <i className="fa fa-ban" /> Cancel QR
                                        </button>
                                      )}
                                      <button
                                        className={s.manualPaidBtn}
                                        onClick={() => openManualPaymentModal(bk)}
                                        title="Record cash/other-provider payment"
                                      >
                                        <i className="fa fa-money" /> Mark Paid
                                      </button>
                                    </>
                                  );
                                })()}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className={s.pagination}>
                    <button
                      className={s.pageBtn}
                      disabled={pendingPage <= 1}
                      onClick={() => handlePendingPageChange(-1)}
                    >
                      <i className="fa fa-chevron-left" /> Prev
                    </button>
                    <span className={s.pageInfo}>
                      Page
                      <input
                        type="number"
                        min={1}
                        max={pendingTotalPages}
                        value={pendingPageInput}
                        onChange={handlePendingPageInputChange}
                        onKeyDown={handlePendingPageInputKeyDown}
                        onBlur={handlePendingPageInputBlur}
                        className={s.pageInput}
                      />
                      of {pendingTotalPages}
                    </span>
                    <button
                      className={s.pageBtn}
                      disabled={pendingPage >= pendingTotalPages}
                      onClick={() => handlePendingPageChange(1)}
                    >
                      Next <i className="fa fa-chevron-right" />
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {/* ===================== PRICING TAB ===================== */}
          {activeTab === 'pricing' && (
            <>
              <div className={s.filterRow}>
                <select
                  className={s.filterSelect}
                  value={pricingClassFilter}
                  onChange={(e) => setPricingClassFilter(e.target.value)}
                >
                  <option value="">All Classes</option>
                  {Array.from({ length: 18 }, (_, i) => i + 1).map((c) => (
                    <option key={c} value={c}>Class {c}</option>
                  ))}
                </select>

                <select
                  className={s.filterSelect}
                  value={pricingPayFilter}
                  onChange={(e) => setPricingPayFilter(e.target.value)}
                  title="If no pay filter is selected, full row is shown"
                >
                  <option value="">All (no pay filter)</option>
                  <option value="driver">Driver Pay</option>
                  <option value="customer">Customer Pay</option>
                </select>

                <button className={s.filterApplyBtn} onClick={() => { setPricingPage(1); fetchPricing(1); }}>
                  <i className="fa fa-search" /> Apply
                </button>
                <button
                  className={s.filterResetBtn}
                  onClick={() => {
                    setPricingClassFilter('');
                    setPricingPayFilter('');
                    setPricingPage(1);
                  }}
                >
                  <i className="fa fa-times" /> Reset
                </button>
              </div>

              <div className={s.pendingHeader}>
                <p className={s.pendingDescription}>
                  <i className="fa fa-filter" />
                  {' '}
                  City filter from the global selector is applied automatically:
                  <strong>
                    {' '}
                    {selectedCities?.length
                      ? selectedCities.join(', ')
                      : (selectedCity || 'All Cities')}
                  </strong>
                </p>
              </div>

              {pricingLoading ? (
                <div className={s.loadingState}>
                  <div className={s.spinner} />
                  <p>Loading pricing rows…</p>
                </div>
              ) : pricingRows.length === 0 ? (
                <div className={s.emptyState}>
                  <i className="fa fa-inbox" />
                  <p>No pricing rows found for the selected filters.</p>
                </div>
              ) : (
                <>
                  <div className={s.txnMeta}>
                    Showing {((pricingPage - 1) * 20) + 1}–{Math.min(pricingPage * 20, pricingTotal)} of {pricingTotal} pricing rows
                  </div>
                  <div className={s.txnTableWrapper}>
                    <table className={s.txnTable}>
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>City</th>
                          <th>Class</th>
                          {pricingPayFilter !== 'driver' && (
                            <>
                              <th>Customer/km</th>
                              <th>Customer Base</th>
                            </>
                          )}
                          {pricingPayFilter !== 'customer' && (
                            <>
                              <th>Driver Base</th>
                              <th>Active Driver/km</th>
                              <th>Dead Driver/km</th>
                              <th>Service Time</th>
                              <th>Active Incl. Base</th>
                            </>
                          )}
                          <th>Waiting Charge</th>
                          <th>Milage</th>
                          <th>EMI</th>
                          <th>Margin Constant</th>
                          <th>Price/Hour</th>
                          <th>Driver Price/Hour</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pricingRows.map((row) => {
                          const draft = pricingEdits[row.id] || {};
                          const valueOf = (field) => (draft[field] ?? row[field] ?? '');

                          return (
                            <tr key={row.id}>
                              <td className={s.monoCell}>#{row.id}</td>
                              <td>{row.city_name}</td>
                              <td>{row.class}</td>

                              {pricingPayFilter !== 'driver' && (
                                <>
                                  <td>
                                    <input
                                      className={s.filterInput}
                                      type="number"
                                      step="0.01"
                                      value={valueOf('customer_price_per_km')}
                                      onChange={(e) => handlePricingFieldChange(row.id, 'customer_price_per_km', e.target.value)}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      className={s.filterInput}
                                      type="number"
                                      step="0.01"
                                      value={valueOf('customer_base_price')}
                                      onChange={(e) => handlePricingFieldChange(row.id, 'customer_base_price', e.target.value)}
                                    />
                                  </td>
                                </>
                              )}

                              {pricingPayFilter !== 'customer' && (
                                <>
                                  <td>
                                    <input
                                      className={s.filterInput}
                                      type="number"
                                      step="0.01"
                                      value={valueOf('driver_base_price')}
                                      onChange={(e) => handlePricingFieldChange(row.id, 'driver_base_price', e.target.value)}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      className={s.filterInput}
                                      type="number"
                                      step="0.01"
                                      value={valueOf('active_driver_pay_km')}
                                      onChange={(e) => handlePricingFieldChange(row.id, 'active_driver_pay_km', e.target.value)}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      className={s.filterInput}
                                      type="number"
                                      step="0.01"
                                      value={valueOf('dead_driver_pay_km')}
                                      onChange={(e) => handlePricingFieldChange(row.id, 'dead_driver_pay_km', e.target.value)}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      className={s.filterInput}
                                      type="number"
                                      step="1"
                                      value={valueOf('service_time')}
                                      onChange={(e) => handlePricingFieldChange(row.id, 'service_time', e.target.value)}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      className={s.filterInput}
                                      type="number"
                                      step="1"
                                      value={valueOf('active_included_in_base')}
                                      onChange={(e) => handlePricingFieldChange(row.id, 'active_included_in_base', e.target.value)}
                                    />
                                  </td>
                                </>
                              )}

                              <td>
                                <input
                                  className={s.filterInput}
                                  type="number"
                                  step="0.01"
                                  value={valueOf('waiting_charge')}
                                  onChange={(e) => handlePricingFieldChange(row.id, 'waiting_charge', e.target.value)}
                                />
                              </td>

                              <td>
                                <input
                                  className={s.filterInput}
                                  type="number"
                                  step="0.01"
                                  value={valueOf('milage')}
                                  onChange={(e) => handlePricingFieldChange(row.id, 'milage', e.target.value)}
                                />
                              </td>

                              <td>
                                <input
                                  className={s.filterInput}
                                  type="number"
                                  step="0.01"
                                  value={valueOf('emi')}
                                  onChange={(e) => handlePricingFieldChange(row.id, 'emi', e.target.value)}
                                />
                              </td>

                              <td>
                                <input
                                  className={s.filterInput}
                                  type="number"
                                  step="0.01"
                                  value={valueOf('margin_constant')}
                                  onChange={(e) => handlePricingFieldChange(row.id, 'margin_constant', e.target.value)}
                                />
                              </td>

                              <td>
                                <input
                                  className={s.filterInput}
                                  type="number"
                                  step="0.01"
                                  value={valueOf('price_per_hour')}
                                  onChange={(e) => handlePricingFieldChange(row.id, 'price_per_hour', e.target.value)}
                                />
                              </td>

                              <td>
                                <input
                                  className={s.filterInput}
                                  type="number"
                                  step="0.01"
                                  value={valueOf('price_per_hour_driver')}
                                  onChange={(e) => handlePricingFieldChange(row.id, 'price_per_hour_driver', e.target.value)}
                                />
                              </td>

                              <td>
                                <button
                                  className={s.viewBtn}
                                  onClick={() => handleSavePricingRow(row)}
                                  disabled={pricingSavingId === row.id}
                                >
                                  {pricingSavingId === row.id ? (
                                    <><i className="fa fa-spinner fa-spin" /> Saving…</>
                                  ) : (
                                    <><i className="fa fa-save" /> Save</>
                                  )}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className={s.pagination}>
                    <button
                      className={s.pageBtn}
                      disabled={pricingPage <= 1}
                      onClick={() => { const p = pricingPage - 1; setPricingPage(p); fetchPricing(p); }}
                    >
                      <i className="fa fa-chevron-left" /> Prev
                    </button>
                    <span className={s.pageInfo}>
                      Page
                      <input
                        type="number"
                        min={1}
                        max={pricingTotalPages}
                        value={pricingPageInput}
                        onChange={handlePricingPageInputChange}
                        onKeyDown={handlePricingPageInputKeyDown}
                        onBlur={handlePricingPageInputBlur}
                        className={s.pageInput}
                      />
                      of {pricingTotalPages}
                    </span>
                    <button
                      className={s.pageBtn}
                      disabled={pricingPage >= pricingTotalPages}
                      onClick={() => { const p = pricingPage + 1; setPricingPage(p); fetchPricing(p); }}
                    >
                      Next <i className="fa fa-chevron-right" />
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {/* ===================== FINANCIAL REPORT TAB (internal, password-gated) ===================== */}
          {activeTab === 'financial_report' && (
            <>
              {!frUnlocked ? (
                <div className={s.statePasswordPanel} style={{ maxWidth: 360 }}>
                  <div className={s.statePasswordTitle}>
                    <i className="fa fa-lock" /> Enter password to continue
                  </div>
                  <input
                    type="password"
                    className={s.statePasswordInput}
                    value={frPasswordInput}
                    onChange={(e) => setFrPasswordInput(e.target.value)}
                    placeholder="Password"
                    onKeyDown={(e) => { if (e.key === 'Enter') handleFrUnlock(); }}
                  />
                  {frUnlockError && (
                    <div style={{ color: '#dc2626', fontSize: '0.78rem', margin: '6px 0' }}>{frUnlockError}</div>
                  )}
                  <button className={s.filterApplyBtn} onClick={handleFrUnlock} disabled={frUnlocking || !frPasswordInput} style={{ marginTop: '8px' }}>
                    {frUnlocking ? <><i className="fa fa-spinner fa-spin" /> Checking...</> : <><i className="fa fa-unlock" /> Unlock</>}
                  </button>
                </div>
              ) : (
                <>
                  <div className={s.filterRow}>
                    <input
                      className={s.filterInput}
                      type="date"
                      value={frStartDate}
                      max={frEndDate}
                      onChange={(e) => setFrStartDate(e.target.value)}
                      title="From date"
                    />
                    <input
                      className={s.filterInput}
                      type="date"
                      value={frEndDate}
                      min={frStartDate}
                      onChange={(e) => setFrEndDate(e.target.value)}
                      title="To date"
                    />
                    <button className={s.filterApplyBtn} onClick={handleFrPreview} disabled={frLoading}>
                      {frLoading ? <><i className="fa fa-spinner fa-spin" /> Loading...</> : <><i className="fa fa-eye" /> Preview</>}
                    </button>
                    <button className={s.filterApplyBtn} onClick={handleFrDownload} disabled={frDownloading}>
                      {frDownloading ? <><i className="fa fa-spinner fa-spin" /> Downloading...</> : <><i className="fa fa-download" /> Download CSV</>}
                    </button>
                    <span style={{ fontSize: '11px', color: '#9ca3af', marginLeft: 'auto', alignSelf: 'center' }}>
                      Internal only &middot; not part of the customer invoice
                    </span>
                  </div>

                  {frError && (
                    <div style={{ color: '#dc2626', fontSize: '0.82rem', margin: '10px 0' }}>{frError}</div>
                  )}

                  {frLoading ? (
                    <div className={s.loadingState}>
                      <div className={s.spinner} />
                      <p>Building report…</p>
                    </div>
                  ) : !frReport ? (
                    <div className={s.emptyState}>
                      <i className="fa fa-file-text-o" />
                      <p>Pick a date range and click Preview</p>
                    </div>
                  ) : frReport.rows.length === 0 ? (
                    <div className={s.emptyState}>
                      <i className="fa fa-inbox" />
                      <p>No completed bookings in this range</p>
                    </div>
                  ) : (
                    <>
                      <div className={s.txnMeta}>
                        {frReport.rows.length} booking{frReport.rows.length === 1 ? '' : 's'} &middot; Total {currency(frReport.totals.total)}
                        {' '}&middot; Platform Fees {currency(frReport.totals.platform_fee)}
                        {' '}&middot; Marketing Discount {currency(frReport.totals.marketing_discount)}
                      </div>

                      <div className={s.txnTableWrapper}>
                        <table className={s.txnTable}>
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Invoice No</th>
                              <th>Booking ID</th>
                              <th>Customer</th>
                              <th>GSTIN</th>
                              <th>Mobile</th>
                              <th>Pickup Address</th>
                              <th>Drop Address</th>
                              <th>Customer Fare</th>
                              <th>Vehicle Charges</th>
                              <th>Platform Fee</th>
                              <th>Marketing Discount</th>
                              <th>CGST</th>
                              <th>SGST</th>
                              <th>Total GST</th>
                              <th>Total</th>
                              <th>Transaction ID</th>
                              <th>Trip ID</th>
                              <th>Pickup Time</th>
                              <th>Drop Time</th>
                            </tr>
                          </thead>
                          <tbody>
                            {frPageRows.map((row) => (
                              <tr key={row.booking_id}>
                                <td style={{ whiteSpace: 'nowrap' }}>{row.date}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>{row.invoice_no}</td>
                                <td>{row.booking_id}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>{row.customer_name || '—'}</td>
                                <td>{row.gstin || '—'}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>{row.mobile_number || '—'}</td>
                                <td style={{ maxWidth: 200, whiteSpace: 'normal' }}>{row.pickup_address || '—'}</td>
                                <td style={{ maxWidth: 200, whiteSpace: 'normal' }}>{row.drop_address || '—'}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>{currency(row.customer_fare)}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>{currency(row.vehicle_charges)}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>{currency(row.platform_fee)}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>{currency(row.marketing_discount)}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>{currency(row.cgst)}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>{currency(row.sgst)}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>{currency(row.total_gst)}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>{currency(row.total)}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>{row.transaction_id || '—'}</td>
                                <td>{row.trip_id ?? '—'}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>{row.pickup_time || '—'}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>{row.drop_time || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {frTotalPages > 1 && (
                        <div className={s.pagination}>
                          <button
                            className={s.pageBtn}
                            disabled={frPage <= 1}
                            onClick={() => setFrPage((p) => Math.max(1, p - 1))}
                          >
                            <i className="fa fa-chevron-left" /> Prev
                          </button>
                          <span className={s.pageInfo}>
                            Page {frPage} of {frTotalPages}
                          </span>
                          <button
                            className={s.pageBtn}
                            disabled={frPage >= frTotalPages}
                            onClick={() => setFrPage((p) => Math.min(frTotalPages, p + 1))}
                          >
                            Next <i className="fa fa-chevron-right" />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* ===================== BOOKING DETAIL SLIDE ===================== */}
        {selectedTxn && (
          <div className={s.detailOverlay} onClick={() => { setSelectedTxn(null); setBookingHistory([]); }}>
            <div className={s.detailPanel} onClick={(e) => e.stopPropagation()}>
              <div className={s.detailHeader}>
                <div>
                  <h3>{(selectedTxn.booking_ids?.length > 1) ? 'Bulk Payment' : `Booking #${selectedTxn.booking_id || selectedTxn.booking_ids?.[0]}`}</h3>
                  <p className={s.detailSubtitle}>{selectedTxn.business_org_name || selectedTxn.customer_name} · {selectedTxn.customer_phone}</p>
                </div>
                <button className={s.closeBtn} onClick={() => { setSelectedTxn(null); setBookingHistory([]); }}>
                  <i className="fa fa-times" />
                </button>
              </div>
              <div className={s.detailBody}>
                <div className={s.detailSummaryRow}>
                  <span className={s.detailSummaryAmount}>{currency(selectedTxn.amount)}</span>
                  <span className={`${s.txnStatus} ${s[statusColor(selectedTxn.status)]}`}>
                    {selectedTxn.status}
                  </span>
                </div>
                {installmentLabel(selectedTxn.installment_type) && (
                  <p className={s.detailPaidAt} style={{ color: '#92400e' }}>
                    <i className="fa fa-adjust" /> {installmentLabel(selectedTxn.installment_type)}
                    {Number(selectedTxn.booking_total || 0) > 0 && (
                      <> · {currency(selectedTxn.amount)} of {currency(selectedTxn.booking_total)} booking total</>
                    )}
                  </p>
                )}
                {selectedTxn.paid_at && (
                  <p className={s.detailPaidAt}>
                    <i className="fa fa-check-circle" /> Paid on {fmtDateTime(selectedTxn.paid_at)}
                  </p>
                )}

                {selectedTxn.status === 'PAID' && (
                  <div className={s.transactionDetails}>
                    <div className={s.detailRow}>
                      <span className={s.detailLabel}>Transaction ID</span>
                      <span className={`${s.detailValue} ${s.mono}`}>{selectedTxn.id}</span>
                    </div>
                    {selectedTxn.booking_ids?.length > 1 && (
                      <div className={s.detailRow}>
                        <span className={s.detailLabel}>Associated Bookings</span>
                        <span className={`${s.detailValue} ${s.mono}`} style={{ fontSize: '0.8rem', color: '#4338ca' }}>
                          {selectedTxn.booking_ids.map((id) => `#${id}`).join(', ')}
                        </span>
                      </div>
                    )}
                    <div className={s.detailRow}>
                      <span className={s.detailLabel}>Provider</span>
                      <span className={s.detailValue}>{selectedTxn.provider || '—'}</span>
                    </div>
                    <div className={s.detailRow}>
                      <span className={s.detailLabel}>Instrument</span>
                      <span className={s.detailValue}>{instrumentLabel(selectedTxn.instrument_type)}</span>
                    </div>
                    {selectedTxn.payment_method && (
                      <div className={s.detailRow}>
                        <span className={s.detailLabel}>Payment Method</span>
                        <span className={s.detailValue}>{selectedTxn.payment_method}</span>
                      </div>
                    )}
                    {selectedTxn.gateway_ref_id && (
                      <div className={s.detailRow}>
                        <span className={s.detailLabel}>Gateway Reference</span>
                        <span className={`${s.detailValue} ${s.mono}`}>{selectedTxn.gateway_ref_id}</span>
                      </div>
                    )}
                    {selectedTxn.gateway_transaction_id && (
                      <div className={s.detailRow}>
                        <span className={s.detailLabel}>Gateway Transaction ID</span>
                        <span className={`${s.detailValue} ${s.mono}`}>{selectedTxn.gateway_transaction_id}</span>
                      </div>
                    )}
                    <div className={s.detailRow}>
                      <span className={s.detailLabel}>Created At</span>
                      <span className={s.detailValue}>{fmtDateTime(selectedTxn.created_at)}</span>
                    </div>
                    <div className={s.detailRow}>
                      <span className={s.detailLabel}>Confirmed At</span>
                      <span className={s.detailValue}>{fmtDateTime(selectedTxn.paid_at)}</span>
                    </div>
                  </div>
                )}

                <div className={s.historySection}>
                  <h4 className={s.historySectionTitle}>
                    <i className="fa fa-history" /> Link History
                    <span className={s.historyCount}>
                      {selectedTxn.txn_count} attempt{selectedTxn.txn_count !== 1 ? 's' : ''}
                    </span>
                  </h4>
                  {historyLoading ? (
                    <div className={s.historyLoading}><div className={s.spinner} /></div>
                  ) : bookingHistory.length === 0 ? (
                    <p className={s.historyEmpty}>No link history found.</p>
                  ) : (
                    <div className={s.historyList}>
                      {bookingHistory.map((h, i) => (
                        <div key={h.id} className={`${s.historyItem} ${s[statusColor(h.status)]}`}>
                          <div className={s.historyItemHeader}>
                            <span className={s.historyAttemptNum}>
                              Attempt {bookingHistory.length - i}
                            </span>
                            <span className={`${s.txnStatus} ${s[statusColor(h.status)]}`}>{h.status}</span>
                          </div>
                          <div className={s.historyItemDetail}>
                            <span className={s.historyItemDate}>
                              <i className="fa fa-inr" /> {currency(h.amount)}
                              {installmentLabel(h.installment_type) && (
                                <> · {installmentLabel(h.installment_type)}</>
                              )}
                            </span>
                            <span className={s.historyItemDate}>
                              <i className="fa fa-clock-o" /> {fmtDateTime(h.created_at)}
                            </span>
                            {h.paid_at && (
                              <span className={`${s.historyItemDate} ${s.green}`}>
                                <i className="fa fa-check" /> {fmtDateTime(h.paid_at)}
                              </span>
                            )}
                          </div>
                          {h.gateway_ref_id && (
                            <div className={s.historyItemRef}>
                              Ref: <span className={s.mono}>{h.gateway_ref_id}</span>
                            </div>
                          )}
                          {h.payment_url && h.status === 'ACTIVE' && (
                            <button
                              className={s.historyItemLink}
                              onClick={() => {
                                navigator.clipboard.writeText(h.payment_url);
                                alert('Payment link copied to clipboard!');
                              }}
                              title="Copy payment link to clipboard"
                            >
                              <i className="fa fa-copy" /> Copy payment link
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {manualPaymentModal && (
          <div className={s.manualModalOverlay} onClick={() => !manualPaySaving && setManualPaymentModal(null)}>
            <div className={s.manualModalCard} onClick={(e) => e.stopPropagation()}>
              <div className={s.manualModalHeader}>
                <h3><i className="fa fa-money" /> Record Manual Payment</h3>
                <button className={s.closeBtn} onClick={() => !manualPaySaving && setManualPaymentModal(null)}>
                  <i className="fa fa-times" />
                </button>
              </div>

              <div className={s.manualModalBody}>
                <div className={s.manualInfoRow}>
                  <span>Booking</span>
                  <strong>#{manualPaymentModal.booking_id}</strong>
                </div>
                <div className={s.manualInfoRow}>
                  <span>Customer</span>
                  <strong>{manualPaymentModal.business_org_name || manualPaymentModal.customer_name || '—'}</strong>
                </div>

                <div className={s.manualFieldRow}>
                  <label>Amount</label>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    value={manualPayForm.amount}
                    onChange={(e) => setManualPayForm((p) => ({ ...p, amount: e.target.value }))}
                  />
                </div>

                <div className={s.manualFieldRow}>
                  <label>Payment mode</label>
                  <div className={s.manualRadioRow}>
                    <label>
                      <input
                        type="radio"
                        name="manualMode"
                        checked={manualPayForm.mode === 'CASH'}
                        onChange={() => setManualPayForm((p) => ({ ...p, mode: 'CASH' }))}
                      /> Cash
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="manualMode"
                        checked={manualPayForm.mode === 'OTHER'}
                        onChange={() => setManualPayForm((p) => ({ ...p, mode: 'OTHER' }))}
                      /> Other Provider
                    </label>
                  </div>
                </div>

                {manualPayForm.mode === 'OTHER' && (
                  <div className={s.manualFieldRow}>
                    <label>Provider name</label>
                    <input
                      type="text"
                      placeholder="e.g. UPI_DIRECT / BANK_TRANSFER"
                      value={manualPayForm.otherProvider}
                      onChange={(e) => setManualPayForm((p) => ({ ...p, otherProvider: e.target.value }))}
                    />
                  </div>
                )}

                <div className={s.manualFieldRow}>
                  <label>Transaction time</label>
                  <input
                    type="datetime-local"
                    value={manualPayForm.transactionTime}
                    onChange={(e) => setManualPayForm((p) => ({ ...p, transactionTime: e.target.value }))}
                  />
                </div>

                <div className={s.manualFieldRow}>
                  <label>Gateway/Txn ID (optional)</label>
                  <input
                    type="text"
                    placeholder="Auto-generated if blank"
                    value={manualPayForm.gatewayTxnId}
                    onChange={(e) => setManualPayForm((p) => ({ ...p, gatewayTxnId: e.target.value }))}
                  />
                </div>

                <div className={s.manualFieldRow}>
                  <label>Notes (optional)</label>
                  <textarea
                    rows={3}
                    placeholder="Collector name / reference / context"
                    value={manualPayForm.notes}
                    onChange={(e) => setManualPayForm((p) => ({ ...p, notes: e.target.value }))}
                  />
                </div>
              </div>

              <div className={s.manualModalFooter}>
                <button className={s.filterResetBtn} onClick={() => setManualPaymentModal(null)} disabled={manualPaySaving}>
                  Cancel
                </button>
                <button className={s.filterApplyBtn} onClick={handleRecordManualPayment} disabled={manualPaySaving}>
                  {manualPaySaving ? <><i className="fa fa-spinner fa-spin" /> Saving...</> : <><i className="fa fa-check" /> Save Manual Payment</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentControlCenter;
